import React, { useState, useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import "./DashboarData.css";
import KPIPerDayChart from "./KPIPerDayChart";
import KPIPerPharseChart from "./KPIPerPharseChart";
import NavbarTrungThu from "../Layout/NavbarTrungThu";
import BT_BDNB from "./BT_BDNB";
import {
  getPbbpYearDateRange,
  summarizePbbpSaleOrders,
} from "./pbbpSaleOrders";
import {
  DASHBOARD_BTT_STORE_IDS,
  DASHBOARD_BONPAS_STORE_IDS,
  DASHBOARD_DANANG_STORE_IDS,
  DASHBOARD_HCM_STORE_IDS,
  fetchDashboardLedgerSummary as fetchLedgerSummary,
  fetchSeasonKpiTargets as fetchKpiTargets,
  getLedgerChartSalesBreakdown,
  getLedgerStoreIdsForLocation,
} from "./dashboardBttApi";
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const productionTargets = {
  all: 63379,
  danang: 51788,
  hcm: 51788,
  bonpas: 11591,
};

const parseNumber = (v) => {
  if (v == null) return 0;
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  const cleaned = String(v)
    .trim()
    .replace(/[^\d.-]/g, "");
  return cleaned === "" ? 0 : Number(cleaned);
};

const buildProgressCircleBackground = (segments, target) => {
  const total = segments.reduce((sum, segment) => sum + parseNumber(segment.value), 0);
  const achievedAngle =
    target > 0 ? Math.min((total / target) * 360, 360) : 0;

  if (total <= 0 || achievedAngle <= 0) {
    return "conic-gradient(#e5e7eb 0deg 360deg)";
  }

  let startAngle = 0;
  const stops = segments
    .filter((segment) => parseNumber(segment.value) > 0)
    .map((segment) => {
      const endAngle =
        startAngle + (parseNumber(segment.value) / total) * achievedAngle;
      const stop = `${segment.color} ${startAngle}deg ${endAngle}deg`;
      startAngle = endAngle;
      return stop;
    });

  stops.push(`#e5e7eb ${startAngle}deg 360deg`);
  return `conic-gradient(${stops.join(", ")})`;
};

const USE_BTT_LEDGER_DASHBOARD = true;
const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}`;
const DASHBOARD_START_DATE = "2026-08-01";
const BTT_CAKE_TYPES = ["BNG", "BNH", "BD"];
const BTT_CAKE_TYPE_META = {
  BNG: { label: "Bánh nướng", color: "#60a5fa", trendKey: "nuong" },
  BNH: { label: "Khác", color: "#6b7280", trendKey: "khac" },
  BD: { label: "Bánh Dẻo", color: "#ef4444", trendKey: "deo" },
};
const isBoxCakeType = (value) =>
  String(value || "").trim().toUpperCase() === "HOP";
const KPI_LOCATION_STORE_MAPPING = {
  all: [1, 2, 3],
  danang: [1],
  hcm: [2],
  bonpas: [3],
};
const ALL_STORE_IDS_PARAM = DASHBOARD_BTT_STORE_IDS.join(",");
const EMPTY_OTHER_QUANTITIES = {
  DaNang: 0,
  BieuTang: 0,
  KhoQC: 0,
  ThanhLyBan: 0,
};

const getTodayDateKey = () => new Date().toISOString().split("T")[0];

const normalizeDateKey = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
};

const getLast7DateKeys = (endDateKey) => {
  const [year, month, day] = normalizeDateKey(endDateKey)
    .split("-")
    .map(Number);
  const endDate = new Date(Date.UTC(year, month - 1, day));

  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(endDate);
    date.setUTCDate(endDate.getUTCDate() - (6 - index));
    return date.toISOString().split("T")[0];
  });
};

const formatShortDate = (dateKey) => {
  const [, month, day] = dateKey.split("-");
  return `${Number(month)}/${Number(day)}`;
};

const buildApiUrl = (path, params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, value);
    }
  });
  return `${API_BASE_URL}${path}${query.toString() ? `?${query}` : ""}`;
};

const fetchJson = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${url}`);
  }
  return response.json();
};

const fetchPbbpSaleOrders = ({ startDate, endDate }) =>
  fetchJson(
    buildApiUrl("/api/sale-orders", {
      NgayBatDau: startDate,
      NgayKetThuc: endDate,
    })
  );

const fetchPbbpData = async ({ startDate, endDate }) => {
  const dateRange = {
    NgayBatDau: startDate,
    NgayKetThuc: endDate,
  };
  const [danangData, bonpasData] = await Promise.all([
    fetchJson(buildApiUrl("/api/DonHangPBBP/DaNang", dateRange)),
    fetchJson(buildApiUrl("/api/DonHangPBBP/Bonpas", dateRange)),
  ]);

  return {
    danang: danangData.DaNang || 0,
    bonpas: bonpasData.Bonpas || 0,
    cakeType: {
      danang: danangData.CakeType || { BD: 0, BNG: 0, BNH: 0 },
      bonpas: bonpasData.CakeType || { BD: 0, BNG: 0, BNH: 0 },
    },
  };
};

const fetchRevenueData = ({ storeIds, endDate }) =>
  fetchJson(
    buildApiUrl("/api/DoanhThu7NgayGanNhat", {
      MaCH: storeIds.join(","),
      NgayKetThuc: endDate,
    })
  );

const fetchProductionProgress = ({ startDate, endDate }) =>
  fetchJson(
    buildApiUrl("/api/tiendosanxuat", {
      NgayBatDau: startDate,
      NgayKetThuc: endDate,
    })
  );

const fetchLegacyDashboardData = async ({
  startDate,
  endDate,
  kpiData,
  productionProgressData,
}) => {
  const dateRange = {
    NgayBatDau: startDate,
    NgayKetThuc: endDate,
  };
  const [
    salesData,
    customOrderData,
    productionData,
    hcmBonpasSalesData,
    hoadonbantheongayData,
  ] = await Promise.all([
    fetchJson(
      buildApiUrl("/api/ThongKeBanBanhTrungThu", {
        MaCH: ALL_STORE_IDS_PARAM,
        ...dateRange,
      })
    ),
    fetchJson(buildApiUrl("/api/soLuongBanDat", dateRange)),
    fetchJson(buildApiUrl("/api/soLuongBanhDaSanXuat", dateRange)),
    fetchJson(buildApiUrl("/api/soLuongBanBanhBanHCM", dateRange)),
    fetchJson(
      buildApiUrl("/api/ThongKeTongHopBanhTrungThu", {
        MaCH: ALL_STORE_IDS_PARAM,
        ...dateRange,
      })
    ),
  ]);

  const dailyData = await Promise.all(
    getLast7DateKeys(endDate).map(async (dateStr) => {
      try {
        const data = await fetchJson(
          buildApiUrl("/api/ThongKeTongHopBanhTrungThu", {
            MaCH: ALL_STORE_IDS_PARAM,
            NgayBatDau: dateStr,
            NgayKetThuc: dateStr,
          })
        );
        return { date: dateStr, data };
      } catch (error) {
        console.error(`Error fetching daily data for ${dateStr}:`, error);
        return { date: dateStr, data: null };
      }
    })
  );

  return {
    kpiData: kpiData || [],
    totalSalesData: salesData || {},
    customOrderData: customOrderData || [],
    productionData: productionData || [],
    hcmBonpasSalesData: hcmBonpasSalesData || [],
    hoadonbantheongayData: hoadonbantheongayData || {},
    productionProgressData: productionProgressData || {},
    dailyData,
    ledgerSummary: null,
  };
};

const createEmptyCakeTotals = () =>
  BTT_CAKE_TYPES.reduce((acc, type) => ({ ...acc, [type]: 0 }), {});

const valuesFromObjectOrArray = (value) =>
  Array.isArray(value) ? value : Object.values(value || {});

const DASHBOARD_STORE_LABEL_OVERRIDES = {
  20: "Kho tổng",
};

const buildStoreNameMap = (storesData = []) =>
  storesData.reduce((acc, store) => {
    acc[String(store.IDCH)] =
      store.MaBravo || store.TenCuaHang || `CH${store.IDCH}`;
    return acc;
  }, {});

const getStoreLabel = (store, storeNameMap) =>
  DASHBOARD_STORE_LABEL_OVERRIDES[Number(store.MaCH)] ||
  storeNameMap[String(store.MaCH)] ||
  store.MaBravo ||
  store.TenCuaHang ||
  `CH${store.MaCH}`;

const buildCakeDoughnutData = (cakeTotals) => ({
  labels: BTT_CAKE_TYPES.map((type) => BTT_CAKE_TYPE_META[type].label),
  datasets: [
    {
      data: BTT_CAKE_TYPES.map((type) => parseNumber(cakeTotals[type])),
      backgroundColor: BTT_CAKE_TYPES.map(
        (type) => BTT_CAKE_TYPE_META[type].color
      ),
    },
  ],
});

const buildBakerySalesDataFromLedger = (ledgerSummary, storesData = []) => {
  const storeNameMap = buildStoreNameMap(storesData);
  const stores = valuesFromObjectOrArray(ledgerSummary?.summaryByStore)
    .map((store) => ({
      ...store,
      total:
        parseNumber(store.MaLBTT?.BNG) +
        parseNumber(store.MaLBTT?.BD) +
        parseNumber(store.MaLBTT?.BNH),
    }))
    .filter((store) => store.total > 0)
    .sort((a, b) => b.total - a.total);

  return {
    labels: stores.map((store) => getStoreLabel(store, storeNameMap)),
    datasets: [
      {
        label: BTT_CAKE_TYPE_META.BNG.label,
        data: stores.map((store) => parseNumber(store.MaLBTT?.BNG)),
        backgroundColor: BTT_CAKE_TYPE_META.BNG.color,
      },
      {
        label: BTT_CAKE_TYPE_META.BD.label,
        data: stores.map((store) => parseNumber(store.MaLBTT?.BD)),
        backgroundColor: BTT_CAKE_TYPE_META.BD.color,
      },
      {
        label: BTT_CAKE_TYPE_META.BNH.label,
        data: stores.map((store) => parseNumber(store.MaLBTT?.BNH)),
        backgroundColor: BTT_CAKE_TYPE_META.BNH.color,
      },
    ],
  };
};

const buildBakeryInventoryDataFromLedger = (
  ledgerSummary,
  storesData = [],
  excludedStoreIds = []
) => {
  const storeNameMap = buildStoreNameMap(storesData);
  const excludedStoreIdSet = new Set(excludedStoreIds.map(Number));
  const stores = valuesFromObjectOrArray(ledgerSummary?.inventoryByStore)
    .map((store) => ({
      ...store,
      total: BTT_CAKE_TYPES.reduce(
        (sum, type) => sum + parseNumber(store.MaLBTT?.[type]),
        0
      ),
    }))
    .filter((store) => !excludedStoreIdSet.has(Number(store.MaCH)))
    .filter((store) => store.total !== 0)
    .sort((a, b) => b.total - a.total);

  return {
    labels: stores.map((store) => getStoreLabel(store, storeNameMap)),
    datasets: [
      {
        label: BTT_CAKE_TYPE_META.BNG.label,
        data: stores.map((store) => parseNumber(store.MaLBTT?.BNG)),
        backgroundColor: BTT_CAKE_TYPE_META.BNG.color,
      },
      {
        label: BTT_CAKE_TYPE_META.BD.label,
        data: stores.map((store) => parseNumber(store.MaLBTT?.BD)),
        backgroundColor: BTT_CAKE_TYPE_META.BD.color,
      },
      {
        label: BTT_CAKE_TYPE_META.BNH.label,
        data: stores.map((store) => parseNumber(store.MaLBTT?.BNH)),
        backgroundColor: BTT_CAKE_TYPE_META.BNH.color,
      },
    ],
  };
};

const buildInventoryByTypeFromLedger = (
  ledgerSummary,
  excludedStoreIds = []
) => {
  const totals = createEmptyCakeTotals();
  const excludedStoreIdSet = new Set(excludedStoreIds.map(Number));
  const inventoryByStore = valuesFromObjectOrArray(ledgerSummary?.inventoryByStore);

  if (inventoryByStore.length > 0) {
    inventoryByStore.forEach((store) => {
      if (excludedStoreIdSet.has(Number(store.MaCH))) return;

      BTT_CAKE_TYPES.forEach((type) => {
        totals[type] += parseNumber(store.MaLBTT?.[type]);
      });
    });
    return buildCakeDoughnutData(totals);
  }

  valuesFromObjectOrArray(ledgerSummary?.inventoryByCakeType).forEach((item) => {
    if (BTT_CAKE_TYPES.includes(item.MaLBTT)) {
      totals[item.MaLBTT] += parseNumber(item.TonCuoiKy);
    }
  });
  return buildCakeDoughnutData(totals);
};

const getKpiTarget = (kpiData, selectedLocation, field = "Target") => {
  const storeIds = KPI_LOCATION_STORE_MAPPING[selectedLocation] || [];
  return kpiData
    .filter((kpi) => storeIds.includes(Number(kpi.ChiNhanh)))
    .reduce((sum, kpi) => sum + parseNumber(kpi[field]), 0);
};

const getCorporateCustomerQuantity = (otherQuantities, selectedLocation) => {
  if (selectedLocation === "all" || selectedLocation === "danang") {
    return parseNumber(otherQuantities.DaNang);
  }
  return 0;
};

const getPbbpCakeTotals = (loyalCustomerData, selectedLocation) => {
  const totals = createEmptyCakeTotals();
  const addCakeType = (cakeType = {}) => {
    BTT_CAKE_TYPES.forEach((type) => {
      totals[type] += parseNumber(cakeType[type]);
    });
  };

  if (selectedLocation === "all" || selectedLocation === "danang") {
    addCakeType(loyalCustomerData.cakeType?.danang);
  }
  if (selectedLocation === "all" || selectedLocation === "bonpas") {
    addCakeType(loyalCustomerData.cakeType?.bonpas);
  }
  return totals;
};

const sumLedgerSalesByStoreIds = (ledgerSummary, storeIds) => {
  const storeIdSet = new Set(storeIds);
  return valuesFromObjectOrArray(ledgerSummary?.summaryByStore).reduce(
    (sum, store) =>
      storeIdSet.has(Number(store.MaCH))
        ? sum + getLedgerChartSalesBreakdown(store).total
        : sum,
    0
  );
};

const buildChartData = ({ ledgerSummary, selectedDate, revenueRows = [] }) => {
  const dateKeys = getLast7DateKeys(selectedDate);
  const summaryByDate = new Map(
    valuesFromObjectOrArray(
      ledgerSummary?.last7Days?.summaryByDate || ledgerSummary?.summaryByDate
    ).map((item) => [normalizeDateKey(item.NgayBan), item])
  );
  const revenueByDate = new Map(
    revenueRows.map((item) => [
      normalizeDateKey(item.Ngay),
      parseNumber(item.TongDoanhThu || item.DoanhThu) / 1000000,
    ])
  );
  const cakeTrends = { deo: [], nuong: [], khac: [] };
  const salesTrend = [];
  const orderTrend = [];
  const revenue = [];

  dateKeys.forEach((dateKey) => {
    const item = summaryByDate.get(dateKey);
    const chartSales = getLedgerChartSalesBreakdown(item);
    salesTrend.push(chartSales.banhBan);
    orderTrend.push(chartSales.banhDat);
    cakeTrends.deo.push(parseNumber(item?.MaLBTT?.BD));
    cakeTrends.nuong.push(parseNumber(item?.MaLBTT?.BNG));
    cakeTrends.khac.push(parseNumber(item?.MaLBTT?.BNH));
    revenue.push(revenueByDate.get(dateKey) || 0);
  });

  return {
    salesTrend,
    orderTrend,
    cakeTrends,
    revenue,
    last7DaysLabels: dateKeys.map(formatShortDate),
  };
};

const buildDashboardMetrics = ({
  ledgerSummary,
  kpiData,
  loyalCustomerData,
  otherQuantities,
  productionProgressData,
  selectedLocation,
  selectedDate,
  revenueRows,
  pbbpOrderStatusSummary,
}) => {
  const ledgerSales = getLedgerChartSalesBreakdown({
    ...ledgerSummary?.grandSummary?.grandTotal,
    byCakeType: ledgerSummary?.summaryByCakeType,
  });
  const ledgerSold = ledgerSales.total;
  const corporateCustomerQuantity = getCorporateCustomerQuantity(
    otherQuantities,
    selectedLocation
  );
  const kpiProgress = ledgerSold + corporateCustomerQuantity;
  const kpiTarget = getKpiTarget(kpiData, selectedLocation) || 200;

  const locationKPI =
    selectedLocation === "all"
      ? [
          {
            name: "ĐN",
            value:
              sumLedgerSalesByStoreIds(
                ledgerSummary,
                DASHBOARD_DANANG_STORE_IDS
              ) + getCorporateCustomerQuantity(otherQuantities, "danang"),
            target: getKpiTarget(kpiData, "danang"),
          },
          {
            name: "HCM",
            value: sumLedgerSalesByStoreIds(
              ledgerSummary,
              DASHBOARD_HCM_STORE_IDS
            ),
            target: getKpiTarget(kpiData, "hcm"),
          },
          {
            name: "Bonpas",
            value:
              sumLedgerSalesByStoreIds(
                ledgerSummary,
                DASHBOARD_BONPAS_STORE_IDS
              ),
            target: getKpiTarget(kpiData, "bonpas"),
          },
        ].map((item) => ({
          name: item.name,
          value: item.value,
          percentage:
            item.target > 0 ? Math.round((item.value / item.target) * 100) : 0,
        }))
      : [
          {
            name:
              selectedLocation === "danang"
                ? "ĐN"
                : selectedLocation === "hcm"
                ? "HCM"
                : "Bonpas",
            value: kpiProgress,
            percentage:
              kpiTarget > 0 ? Math.round((kpiProgress / kpiTarget) * 100) : 0,
          },
        ];

  const cakeTotals = createEmptyCakeTotals();
  valuesFromObjectOrArray(ledgerSummary?.summaryByCakeType).forEach((item) => {
    if (BTT_CAKE_TYPES.includes(item.MaLBTT)) {
      cakeTotals[item.MaLBTT] += parseNumber(item.SoLuongBan);
    }
  });
  const pbbpCakeTotals = getPbbpCakeTotals(loyalCustomerData, selectedLocation);
  BTT_CAKE_TYPES.forEach((type) => {
    cakeTotals[type] += pbbpCakeTotals[type];
  });

  const totalProduction =
    parseNumber(productionProgressData?.Danang) +
    parseNumber(productionProgressData?.Bonpas);
  const productionTarget = productionTargets[selectedLocation];
  const productionProgress =
    selectedLocation === "all"
      ? [
          {
            name: "Tổng SX",
            value: totalProduction,
            percentage:
              productionTarget > 0
                ? Math.round((totalProduction / productionTarget) * 100)
                : 0,
          },
        ]
      : [
          {
            name: selectedLocation === "bonpas" ? "Bonpas" : "ĐN & HCM",
            value:
              selectedLocation === "bonpas"
                ? parseNumber(productionProgressData?.Bonpas)
                : parseNumber(productionProgressData?.Danang),
            percentage:
              productionTarget > 0
                ? Math.round(
                    ((selectedLocation === "bonpas"
                      ? parseNumber(productionProgressData?.Bonpas)
                      : parseNumber(productionProgressData?.Danang)) /
                      productionTarget) *
                      100
                  )
                : 0,
          },
        ];

  const chartData = buildChartData({
    ledgerSummary,
    selectedDate,
    revenueRows,
  });

  return {
    kpiProgress,
    kpiTarget,
    locationKPI,
    orderTypeKPI: [
      {
        name: "Bánh bán",
        value: ledgerSales.banhBan,
        percentage:
          getKpiTarget(kpiData, selectedLocation, "BanhBan") > 0
            ? Math.round(
                (ledgerSales.banhBan /
                  getKpiTarget(kpiData, selectedLocation, "BanhBan")) *
                  100
              )
            : 0,
      },
      {
        name: "Bánh đặt",
        value: ledgerSales.banhDat,
        percentage:
          getKpiTarget(kpiData, selectedLocation, "BanhDat") > 0
            ? Math.round(
                (ledgerSales.banhDat /
                  getKpiTarget(kpiData, selectedLocation, "BanhDat")) *
                  100
              )
            : 0,
      },
      {
        name: "Bánh đặt PBBP",
        value: pbbpOrderStatusSummary.total,
        unconfirmedValue: pbbpOrderStatusSummary.unconfirmed,
        depositedPendingConfirmationValue:
          pbbpOrderStatusSummary.depositedPendingConfirmation,
        confirmedCompletedValue: pbbpOrderStatusSummary.confirmedCompleted,
        percentage: 0,
      },
    ],
    cakeTypeKPI: [
      {
        name: BTT_CAKE_TYPE_META.BNG.label,
        value: cakeTotals.BNG,
        percentage:
          getKpiTarget(kpiData, selectedLocation, "BNG") > 0
            ? Math.round(
                (cakeTotals.BNG /
                  getKpiTarget(kpiData, selectedLocation, "BNG")) *
                  100
              )
            : 0,
      },
      {
        name: BTT_CAKE_TYPE_META.BNH.label,
        value: cakeTotals.BNH,
        percentage:
          getKpiTarget(kpiData, selectedLocation, "BNH") > 0
            ? Math.round(
                (cakeTotals.BNH /
                  getKpiTarget(kpiData, selectedLocation, "BNH")) *
                  100
              )
            : 0,
      },
      {
        name: BTT_CAKE_TYPE_META.BD.label,
        value: cakeTotals.BD,
        percentage:
          getKpiTarget(kpiData, selectedLocation, "BD") > 0
            ? Math.round(
                (cakeTotals.BD /
                  getKpiTarget(kpiData, selectedLocation, "BD")) *
                  100
              )
            : 0,
      },
    ],
    productionProgress,
    salesTrend: chartData.salesTrend,
    orderTrend: chartData.orderTrend,
    cakeTrends: chartData.cakeTrends,
    revenue: chartData.revenue,
    last7DaysLabels: chartData.last7DaysLabels,
    bakeryLocationData: buildBakerySalesDataFromLedger(ledgerSummary),
    inventoryByType: buildInventoryByTypeFromLedger(
      ledgerSummary,
      DASHBOARD_HCM_STORE_IDS
    ),
  };
};

const DashboardDataNew = () => {
  // State cho location và khoảng thời gian
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [selectedStartDate, setSelectedStartDate] = useState(
    DASHBOARD_START_DATE
  );
  const [selectedEndDate, setSelectedEndDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const {
    startDate: pbbpStartDate,
    endDate: pbbpEndDate,
  } = getPbbpYearDateRange(selectedStartDate, selectedEndDate);
  const [topN, setTopN] = useState("all");

  // Chỉ cho phép request bộ lọc mới nhất cập nhật dashboard.
  const dashboardRequestIdRef = useRef(0);
  const revenueRequestIdRef = useRef(0);
  const pbbpRequestIdRef = useRef(0);
  const pbbpSaleOrdersRequestIdRef = useRef(0);
  const [dashboardData, setDashboardData] = useState({
    kpiData: [],
    totalSalesData: {},
    customOrderData: [],
    productionData: [],
    hcmBonpasSalesData: [],
    hoadonbantheongayData: [],
    productionProgressData: {}, // Add new state for production progress
    ledgerSummary: null,
    loading: true,
    error: null,
  });

  const [loadingStates, setLoadingStates] = useState({
    bakeryLocation: true,
    inventory: true,
    dashboard: true,
  });

  const [bakeryLocationData, setBakeryLocationData] = useState({
    labels: [],
    datasets: [
      {
        label: "Bánh nướng",
        data: [],
        backgroundColor: "#60a5fa",
      },
      {
        label: "Bánh Dẻo",
        data: [],
        backgroundColor: "#ef4444",
      },
      {
        label: "Khác",
        data: [],
        backgroundColor: "#6b7280",
      },
    ],
  });

  const [inventoryByTypeData, setInventoryByTypeData] = useState({
    labels: ["Bánh Nướng", "Bánh Nhí", "Bánh Dẻo"],
    datasets: [
      {
        data: [1225, 366, 22],
        backgroundColor: ["#60a5fa", "#6b7280", "#ef4444"],
      },
    ],
  });

  const [bakeryInventoryData, setBakeryInventoryData] = useState({
    labels: [],
    datasets: [
      {
        label: "Bánh nướng",
        data: [],
        backgroundColor: "#60a5fa",
      },
      {
        label: "Bánh Dẻo",
        data: [],
        backgroundColor: "#ef4444",
      },
      {
        label: "Khác",
        data: [],
        backgroundColor: "#6b7280",
      },
    ],
  });

  const [storesData, setStoresData] = useState([]);

  const [danangRevenueData, setDanangRevenueData] = useState([]);

  const [inventoryDataRaw, setInventoryDataRaw] = useState(null);

  // Mapping location to store IDs
  const locationStoreMapping = {
    all: [1, 2, 3], // Tất cả cửa hàng
    danang: [1], // Đà Nẵng
    hcm: [2], // Hồ Chí Minh
    bonpas: [3], // Bonpas
  };

  // Mock data cho các biểu đồ - giữ lại cho fallback
  const mockDataByLocation = {
    all: {
      kpiProgress: 201,
      kpiTarget: 200,
      locationKPI: [
        { name: "ĐN", value: 41, percentage: 1 },
        { name: "HCM", value: 23, percentage: 2 },
      ],
      orderTypeKPI: [
        { name: "Bánh bán", value: 170, percentage: 14 },
        { name: "Bánh đặt", value: 31, percentage: 3 },
      ],
      cakeTypeKPI: [
        { name: "Bánh nướng", value: 174, percentage: 4 },
        { name: "Khác", value: 21, percentage: 3 },
        { name: "Bánh Dẻo", value: 6, percentage: 1 },
      ],
      productionProgress: [
        { name: "ĐN", value: 41, percentage: 1 },
        { name: "HCM", value: 23, percentage: 2 },
      ],
      salesTrend: [29, 35, 36, 25, 22, 17, 6],
      orderTrend: [27, 1, 1, 0, 0, 1, 2],
      cakeTrends: {
        deo: [5, 12, 35, 7, 24, 22, 18],
        nuong: [39, 0, 1, 1, 1, 0, 8],
        khac: [0, 0, 0, 28, 0, 0, 0],
      },
      revenue: [2.0, 3.0, 1.4, 2.7, 2.5, 1.9, 0.8],
      inventoryByType: {
        labels: ["Bánh Nướng", "Bánh Nhí", "Bánh Dẻo"],
        datasets: [
          {
            data: [33, 21, 5],
            backgroundColor: ["#60a5fa", "#6b7280", "#ef4444"],
          },
        ],
      },
    },
    danang: {
      kpiProgress: 98,
      kpiTarget: 100,
      locationKPI: [{ name: "ĐN", value: 98, percentage: 98 }],
      orderTypeKPI: [
        { name: "Bánh bán", value: 85, percentage: 85 },
        { name: "Bánh đặt", value: 13, percentage: 13 },
      ],
      cakeTypeKPI: [
        { name: "Bánh nướng", value: 87, percentage: 87 },
        { name: "Khác", value: 8, percentage: 8 },
        { name: "Bánh Dẻo", value: 3, percentage: 3 },
      ],
      productionProgress: [{ name: "ĐN", value: 98, percentage: 98 }],
      salesTrend: [15, 18, 20, 12, 10, 8, 3],
      orderTrend: [13, 0, 1, 0, 0, 0, 1],
      cakeTrends: {
        deo: [2, 6, 18, 3, 12, 11, 9],
        nuong: [20, 0, 0, 1, 0, 0, 4],
        khac: [0, 0, 0, 14, 0, 0, 0],
      },
      revenue: [1.2, 1.8, 0.8, 1.5, 1.3, 1.0, 0.4],
      inventoryByType: {
        labels: ["Bánh Nướng", "Bánh Nhí", "Bánh Dẻo"],
        datasets: [
          {
            data: [18, 10, 2],
            backgroundColor: ["#60a5fa", "#6b7280", "#ef4444"],
          },
        ],
      },
    },
    hcm: {
      kpiProgress: 103,
      kpiTarget: 100,
      locationKPI: [{ name: "HCM", value: 103, percentage: 103 }],
      orderTypeKPI: [
        { name: "Bánh bán", value: 85, percentage: 82 },
        { name: "Bánh đặt", value: 18, percentage: 17 },
      ],
      cakeTypeKPI: [
        { name: "Bánh nướng", value: 87, percentage: 84 },
        { name: "Khác", value: 13, percentage: 13 },
        { name: "Bánh Dẻo", value: 3, percentage: 3 },
      ],
      productionProgress: [{ name: "HCM", value: 103, percentage: 103 }],
      salesTrend: [14, 17, 16, 13, 12, 9, 3],
      orderTrend: [14, 1, 0, 0, 0, 1, 1],
      cakeTrends: {
        deo: [3, 6, 17, 4, 12, 11, 9],
        nuong: [19, 0, 1, 0, 1, 0, 4],
        khac: [0, 0, 0, 14, 0, 0, 0],
      },
      revenue: [0.8, 1.2, 0.6, 1.2, 1.2, 0.9, 0.4],
      inventoryByType: {
        labels: ["Bánh Nướng", "Bánh Nhí", "Bánh Dẻo"],
        datasets: [
          {
            data: [15, 11, 3],
            backgroundColor: ["#60a5fa", "#6b7280", "#ef4444"],
          },
        ],
      },
    },
    bonpas: {
      kpiProgress: 67,
      kpiTarget: 80,
      locationKPI: [{ name: "Bonpas", value: 67, percentage: 84 }],
      orderTypeKPI: [
        { name: "Bánh bán", value: 55, percentage: 82 },
        { name: "Bánh đặt", value: 12, percentage: 18 },
      ],
      cakeTypeKPI: [
        { name: "Bánh nướng", value: 45, percentage: 67 },
        { name: "Khác", value: 15, percentage: 22 },
        { name: "Bánh Dẻo", value: 7, percentage: 10 },
      ],
      productionProgress: [{ name: "Bonpas", value: 67, percentage: 84 }],
      salesTrend: [8, 12, 10, 9, 8, 6, 2],
      orderTrend: [7, 1, 0, 0, 0, 1, 1],
      cakeTrends: {
        deo: [1, 3, 8, 2, 6, 5, 4],
        nuong: [10, 0, 0, 0, 0, 0, 2],
        khac: [0, 0, 0, 8, 0, 0, 0],
      },
      revenue: [0.5, 0.8, 0.4, 0.7, 0.6, 0.5, 0.2],
      inventoryByType: {
        labels: ["Bánh Nướng", "Bánh Nhí", "Bánh Dẻo"],
        datasets: [
          {
            data: [12, 8, 4],
            backgroundColor: ["#60a5fa", "#6b7280", "#ef4444"],
          },
        ],
      },
    },
  };

  // Fetch data from APIs
  useEffect(() => {
    const fetchDashboardData = async () => {
      const requestId = ++dashboardRequestIdRef.current;

      try {
        setDashboardData((prev) => ({ ...prev, loading: true, error: null }));
        setLoadingStates((prev) => ({ ...prev, dashboard: true }));

        const startDate = selectedStartDate || DASHBOARD_START_DATE;
        const endDate = selectedEndDate || getTodayDateKey();
        const [ledgerKpiData, ledgerProductionProgressData] =
          await Promise.all([
            fetchKpiTargets(),
            fetchProductionProgress({ startDate, endDate }),
          ]);

        if (USE_BTT_LEDGER_DASHBOARD) {
          try {
            const ledgerSummary = await fetchLedgerSummary({
              storeIds: getLedgerStoreIdsForLocation(
                selectedLocation,
                storesData
              ),
              startDate,
              endDate,
            });

            if (ledgerSummary?.success) {
              if (requestId !== dashboardRequestIdRef.current) return;

              setDashboardData({
                kpiData: ledgerKpiData || [],
                totalSalesData: {},
                customOrderData: [],
                productionData: [],
                hcmBonpasSalesData: [],
                hoadonbantheongayData: {},
                productionProgressData: ledgerProductionProgressData || {},
                dailyData: [],
                ledgerSummary,
                loading: false,
                error: null,
              });
              setInventoryDataRaw(ledgerSummary);
              setLoadingStates((prev) => ({
                ...prev,
                dashboard: false,
                bakeryLocation: false,
                inventory: false,
              }));
              return;
            }
          } catch (ledgerError) {
            console.error("Ledger dashboard API failed, using fallback:", ledgerError);
          }

          const legacyData = await fetchLegacyDashboardData({
            startDate,
            endDate,
            kpiData: ledgerKpiData,
            productionProgressData: ledgerProductionProgressData,
          });

          if (requestId !== dashboardRequestIdRef.current) return;

          setDashboardData({
            ...legacyData,
            loading: false,
            error: null,
          });
          setLoadingStates((prev) => ({ ...prev, dashboard: false }));
          return;
        }

      } catch (error) {
        if (requestId !== dashboardRequestIdRef.current) return;

        console.error("Error fetching dashboard data:", error);
        setDashboardData((prev) => ({
          ...prev,
          loading: false,
          error: "Không thể tải dữ liệu",
        }));
      }
    };

    fetchDashboardData();
  }, [selectedStartDate, selectedEndDate, selectedLocation, storesData]);

  // Fetch bakery sales data
  useEffect(() => {
    const fetchBakerySalesData = async () => {
      if (USE_BTT_LEDGER_DASHBOARD && dashboardData.loading) {
        return;
      }

      if (
        USE_BTT_LEDGER_DASHBOARD &&
        dashboardData.ledgerSummary?.success
      ) {
        setBakeryLocationData(
          buildBakerySalesDataFromLedger(
            dashboardData.ledgerSummary,
            storesData
          )
        );
        setLoadingStates((prev) => ({ ...prev, bakeryLocation: false }));
        return;
      }

      try {
        setLoadingStates((prev) => ({ ...prev, bakeryLocation: true }));
        const data = await fetchJson(
          buildApiUrl("/api/ThongKeTongHopBanhTrungThu", {
            MaCH: ALL_STORE_IDS_PARAM,
            NgayBatDau: selectedStartDate,
            NgayKetThuc: selectedEndDate,
          })
        );

        if (data.success && data.summaryByStore) {
          // Create storeMap from storesData to get Bravo codes
          const storeMap = storesData.reduce((acc, store) => {
            acc[store.IDCH.toString()] =
              store.MaBravo || store.TenCuaHang || `CH${store.IDCH}`;
            return acc;
          }, {});

          const labels = [];
          const banhNuongData = [];
          const banhDeoData = [];
          const khacData = [];

          Object.values(data.summaryByStore).forEach((store) => {
            const totalSales =
              (store.MaLBTT?.BNG || 0) +
              (store.MaLBTT?.BD || 0) +
              (store.MaLBTT?.BNH || 0);
            if (totalSales > 0) {
              // Use Bravo code from storeMap, fallback to default if not found
              labels.push(storeMap[store.MaCH] || `Cửa hàng ${store.MaCH}`);
              banhNuongData.push(store.MaLBTT?.BNG || 0);
              banhDeoData.push(store.MaLBTT?.BD || 0);
              khacData.push(store.MaLBTT?.BNH || 0);
            }
          });

          setBakeryLocationData({
            labels,
            datasets: [
              {
                label: "Bánh nướng",
                data: banhNuongData,
                backgroundColor: "#60a5fa",
              },
              {
                label: "Bánh Dẻo",
                data: banhDeoData,
                backgroundColor: "#ef4444",
              },
              {
                label: "Khác",
                data: khacData,
                backgroundColor: "#6b7280",
              },
            ],
          });
        }
        setLoadingStates((prev) => ({ ...prev, bakeryLocation: false }));
      } catch (error) {
        console.error("Error fetching bakery sales data:", error);
        setLoadingStates((prev) => ({ ...prev, bakeryLocation: false }));
      }
    };

    fetchBakerySalesData();
  }, [
    storesData,
    dashboardData.loading,
    dashboardData.ledgerSummary,
    selectedStartDate,
    selectedEndDate,
  ]);

  // Fetch inventory data
  useEffect(() => {
    const fetchInventoryData = async () => {
      if (USE_BTT_LEDGER_DASHBOARD && dashboardData.loading) {
        return;
      }

      if (
        USE_BTT_LEDGER_DASHBOARD &&
        dashboardData.ledgerSummary?.success
      ) {
        const ledgerInventoryByType = buildInventoryByTypeFromLedger(
          dashboardData.ledgerSummary
        );
        setInventoryByTypeData(ledgerInventoryByType);
        setBakeryInventoryData(
          buildBakeryInventoryDataFromLedger(
            dashboardData.ledgerSummary,
            storesData,
            DASHBOARD_HCM_STORE_IDS
          )
        );
        setInventoryDataRaw(dashboardData.ledgerSummary);
        setLoadingStates((prev) => ({ ...prev, inventory: false }));
        return;
      }

      try {
        setLoadingStates((prev) => ({ ...prev, inventory: true }));
        const data = await fetchJson(
          buildApiUrl("/api/TonKhoConLai", {
            MaCH: ALL_STORE_IDS_PARAM,
            NgayTruyVan: selectedEndDate,
          })
        );

        if (data.success && data.dataByStore) {
          // Flatten dataByStore into inventory array
          const inventory = Object.values(data.dataByStore)
            .flat()
            .filter((item) => !isBoxCakeType(item.MaLBTT));

          const groupedData = inventory.reduce((acc, item) => {
            const { MaLBTT, TonConLai } = item;
            if (!acc[MaLBTT]) {
              acc[MaLBTT] = 0;
            }
            acc[MaLBTT] += Math.max(0, TonConLai); // Số âm tính là 0
            return acc;
          }, {});

          // Define fixed order for keys to ensure consistent labels and colors
          const keyOrder = ["BNG", "BNH", "BD"];

          const inventoryLabels = [];
          const dataValues = [];
          const backgroundColors = [];

          keyOrder.forEach((key) => {
            if (groupedData[key] !== undefined) {
              inventoryLabels.push(
                key === "BNG"
                  ? "Bánh Nướng"
                  : key === "BNH"
                  ? "Bánh Nhí"
                  : key === "BD"
                  ? "Bánh Dẻo"
                  : "Khác"
              );
              dataValues.push(groupedData[key]);
              backgroundColors.push(
                key === "BNG"
                  ? "#60a5fa" // Blue for Bánh Nướng
                  : key === "BNH"
                  ? "#6b7280" // Gray for Bánh Nhí
                  : key === "BD"
                  ? "#ef4444" // Red for Bánh Dẻo
                  : "#000000" // Black for any other (fallback)
              );
            }
          });

          // If there are extra keys not in keyOrder, add them as "Khác"
          Object.keys(groupedData).forEach((key) => {
            if (!keyOrder.includes(key)) {
              inventoryLabels.push("Khác");
              dataValues.push(groupedData[key]);
              backgroundColors.push("#000000"); // Fallback color
            }
          });

          const inventoryData = {
            labels: inventoryLabels,
            datasets: [
              {
                data: dataValues,
                backgroundColor: backgroundColors,
              },
            ],
          };

          setInventoryByTypeData(inventoryData);

          console.log(
            "🔍 INVENTORY BY TYPE - labels:",
            inventoryData.labels,
            "data:",
            inventoryData.datasets[0].data
          );

          // Calculate bakery inventory data by store
          const groupedByStore = inventory.reduce((acc, item) => {
            const { MaCH, MaLBTT, TonConLai } = item;
            if (!acc[MaCH]) {
              acc[MaCH] = {};
            }
            if (!acc[MaCH][MaLBTT]) {
              acc[MaCH][MaLBTT] = 0;
            }
            acc[MaCH][MaLBTT] += Math.max(0, TonConLai);
            return acc;
          }, {});

          const storeMap = storesData.reduce((acc, store) => {
            acc[store.IDCH.toString()] =
              store.MaBravo || store.TenCuaHang || `CH${store.IDCH}`;
            return acc;
          }, {});

          const labels = Object.keys(groupedByStore).map(
            (maCH) => storeMap[maCH] || `CH${maCH}`
          );
          const bngData = labels.map((_, index) => {
            const maCH = Object.keys(groupedByStore)[index];
            return groupedByStore[maCH].BNG || 0;
          });
          const bnhData = labels.map((_, index) => {
            const maCH = Object.keys(groupedByStore)[index];
            return groupedByStore[maCH].BNH || 0;
          });
          const bdData = labels.map((_, index) => {
            const maCH = Object.keys(groupedByStore)[index];
            return groupedByStore[maCH].BD || 0;
          });

          setBakeryInventoryData({
            labels,
            datasets: [
              {
                label: "Bánh nướng",
                data: bngData,
                backgroundColor: "#60a5fa",
              },
              {
                label: "Bánh Dẻo",
                data: bdData,
                backgroundColor: "#ef4444",
              },
              {
                label: "Khác",
                data: bnhData,
                backgroundColor: "#6b7280",
              },
            ],
          });
        }
        setLoadingStates((prev) => ({ ...prev, inventory: false }));
      } catch (error) {
        console.error("Error fetching inventory data:", error);
        setLoadingStates((prev) => ({ ...prev, inventory: false }));
      }
    };

    fetchInventoryData();
  }, [
    storesData,
    dashboardData.loading,
    dashboardData.ledgerSummary,
    selectedEndDate,
  ]);

  // Fetch stores data for mapping MaCH to MaBravo
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/stores`);
        const data = await response.json();
        setStoresData(data);
      } catch (error) {
        console.error("Error fetching stores:", error);
      }
    };

    fetchStores();
  }, []);

  // Fetch Đà Nẵng revenue data
  useEffect(() => {
    const fetchDanangRevenue = async () => {
      const requestId = ++revenueRequestIdRef.current;

      try {
        const data = await fetchRevenueData({
          storeIds: getLedgerStoreIdsForLocation(
            selectedLocation,
            storesData
          ),
          endDate: selectedEndDate,
        });
        if (requestId !== revenueRequestIdRef.current) return;

        console.log(
          "🔍 DEBUG - Raw API response for DoanhThu7NgayGanNhat:",
          data
        ); // Add logging to inspect the response
        if (data.success && data.data) {
          const sortedData = [...data.data].sort(
            (a, b) => new Date(a.Ngay) - new Date(b.Ngay)
          );
          console.log(
            "🔍 DEBUG - Processed last7Days for Đà Nẵng revenue:",
            sortedData
          ); // Log processed data
          setDanangRevenueData(sortedData);
        } else {
          console.warn("⚠️ API response unsuccessful or missing data.data");
        }
      } catch (error) {
        if (requestId !== revenueRequestIdRef.current) return;

        console.error("Error fetching Đà Nẵng revenue:", error);
        setDanangRevenueData([]); // Reset to empty array on error
      }
    };

    fetchDanangRevenue();
  }, [selectedEndDate, selectedLocation, storesData]);

  // Add state for loyal customer data
  const [loyalCustomerData, setLoyalCustomerData] = useState({
    danang: 0,
    bonpas: 0,
    cakeType: {
      danang: { BD: 0, BNG: 0, BNH: 0 },
      bonpas: { BD: 0, BNG: 0, BNH: 0 },
    },
  });
  const [pbbpSaleOrders, setPbbpSaleOrders] = useState([]);
  const [otherQuantities, setOtherQuantities] = useState(
    EMPTY_OTHER_QUANTITIES
  );

  // Fetch loyal customer data
  useEffect(() => {
    const fetchLoyalCustomerData = async () => {
      const requestId = ++pbbpRequestIdRef.current;

      try {
        const data = await fetchPbbpData({
          startDate: pbbpStartDate,
          endDate: pbbpEndDate,
        });
        if (requestId !== pbbpRequestIdRef.current) return;

        setLoyalCustomerData(data);
      } catch (error) {
        if (requestId !== pbbpRequestIdRef.current) return;

        console.error("Error fetching loyal customer data:", error);
      }
    };

    fetchLoyalCustomerData();
  }, [pbbpStartDate, pbbpEndDate]);

  useEffect(() => {
    const loadPbbpSaleOrders = async () => {
      const requestId = ++pbbpSaleOrdersRequestIdRef.current;

      try {
        const payload = await fetchPbbpSaleOrders({
          startDate: pbbpStartDate,
          endDate: pbbpEndDate,
        });
        if (requestId !== pbbpSaleOrdersRequestIdRef.current) return;

        setPbbpSaleOrders(
          payload?.success && Array.isArray(payload.data) ? payload.data : []
        );
      } catch (error) {
        if (requestId !== pbbpSaleOrdersRequestIdRef.current) return;

        console.error("Error fetching PBBP sale orders:", error);
        setPbbpSaleOrders([]);
      }
    };

    loadPbbpSaleOrders();
  }, [pbbpStartDate, pbbpEndDate]);

  // Calculate dashboard metrics from API data
  const calculateDashboardMetrics = () => {
    if (dashboardData.loading || dashboardData.error) {
      return mockDataByLocation[selectedLocation]; // Fallback to mock data
    }

    const {
      kpiData,
      totalSalesData,
      customOrderData,
      productionData,
      hcmBonpasSalesData,
      hoadonbantheongayData,
      productionProgressData, // Destructure new data
      dailyData,
    } = dashboardData;
    const pbbpOrderStatusSummary = summarizePbbpSaleOrders(
      pbbpSaleOrders,
      selectedLocation
    );

    if (
      USE_BTT_LEDGER_DASHBOARD &&
      dashboardData.ledgerSummary?.success
    ) {
      return buildDashboardMetrics({
        ledgerSummary: dashboardData.ledgerSummary,
        kpiData,
        loyalCustomerData,
        otherQuantities,
        productionProgressData,
        selectedLocation,
        selectedDate: selectedEndDate,
        revenueRows: danangRevenueData,
        pbbpOrderStatusSummary,
      });
    }

    console.log("🔍 DEBUG - RAW DATA FROM APIs:");
    console.log(
      "- hcmBonpasSalesData length:",
      hcmBonpasSalesData?.length || 0
    );
    console.log(
      "- hcmBonpasSalesData sample:",
      hcmBonpasSalesData?.slice(0, 3)
    );
    console.log("- customOrderData length:", customOrderData?.length || 0);
    console.log(
      "- hoadonbantheongayData success:",
      hoadonbantheongayData?.success
    );

    console.log("=== DASHBOARD METRICS CALCULATION ===");

    // Tính tồn kho theo loại bánh cho Bonpas và HCM
    const getInventoryByTypeForLocation = () => {
      if (selectedLocation === "all") {
        // Sum inventories from danang, hcm, bonpas
        const danangInv = inventoryByTypeData;
        const hcmInv = (() => {
          const cakeTypes = ["BNG", "BNH", "BD"];
          const locationKey = "HoChiMinh";
          // use parseNumber to avoid NaN from formatted strings
          const produced = cakeTypes.map((type) =>
            parseNumber(productionData[type]?.[locationKey])
          );
          let sold = [0, 0, 0];
          if (Array.isArray(hcmBonpasSalesData)) {
            hcmBonpasSalesData.forEach((item) => {
              if (item.Status === 1 && item.LoaiPhieu === 2 && item.NguoiNhan) {
                const isHcm =
                  item.NguoiNhan === 163 || item.NguoiNhan === "163";
                if (isHcm) {
                  if (item.MaLBTT === "BNG")
                    sold[0] += parseNumber(item.SoLuong);
                  else if (item.MaLBTT === "BNH")
                    sold[1] += parseNumber(item.SoLuong);
                  else if (item.MaLBTT === "BD")
                    sold[2] += parseNumber(item.SoLuong);
                }
              }
            });
          }
          if (Array.isArray(customOrderData)) {
            customOrderData.forEach((order) => {
              if (
                order.Status === 1 &&
                order.LoaiPhieu === 1 &&
                order.NguoiNhan
              ) {
                const isHcm = [
                  163,
                  74,
                  128,
                  116,
                  111,
                  90,
                  78,
                  "163",
                  "74",
                  "128",
                  "116",
                  "111",
                  "90",
                  "78",
                ].includes(order.NguoiNhan);
                if (isHcm) {
                  if (order.MaLBTT === "BNG") sold[0] += order.SoLuong || 0;
                  else if (order.MaLBTT === "BNH")
                    sold[1] += order.SoLuong || 0;
                  else if (order.MaLBTT === "BD") sold[2] += order.SoLuong || 0;
                }
              }
            });
          }
          const inventory = produced.map((qty, idx) => {
            const p = Number(qty || 0);
            const s = Number(sold[idx] || 0);
            return Math.max(0, p - s);
          });
          return {
            labels: ["Bánh Nướng", "Bánh Nhí", "Bánh Dẻo"],
            datasets: [
              {
                data: inventory,
                backgroundColor: ["#60a5fa", "#6b7280", "#ef4444"],
              },
            ],
          };
        })();
        const bonpasInv = (() => {
          const cakeTypes = ["BNG", "BNH", "BD"];
          const locationKey = "Bonpas";
          // use parseNumber to avoid NaN from formatted strings
          const produced = cakeTypes.map((type) =>
            parseNumber(productionData[type]?.[locationKey])
          );
          let sold = [0, 0, 0];
          if (Array.isArray(hcmBonpasSalesData)) {
            hcmBonpasSalesData.forEach((item) => {
              if (item.Status === 1 && item.LoaiPhieu === 2 && item.NguoiNhan) {
                const isBonpas =
                  item.NguoiNhan === 1663 || item.NguoiNhan === "1663";
                if (isBonpas) {
                  if (item.MaLBTT === "BNG")
                    sold[0] += parseNumber(item.SoLuong);
                  else if (item.MaLBTT === "BNH")
                    sold[1] += parseNumber(item.SoLuong);
                  else if (item.MaLBTT === "BD")
                    sold[2] += parseNumber(item.SoLuong);
                }
              }
            });
          }
          if (Array.isArray(customOrderData)) {
            customOrderData.forEach((order) => {
              if (
                order.Status === 1 &&
                order.LoaiPhieu === 1 &&
                order.NguoiNhan
              ) {
                const isBonpas = [
                  1663,
                  1,
                  3,
                  4,
                  5,
                  7,
                  "1663",
                  "1",
                  "3",
                  "4",
                  "5",
                  "7",
                ].includes(order.NguoiNhan);
                if (isBonpas) {
                  if (order.MaLBTT === "BNG")
                    sold[0] += Number(order.SoLuong || 0);
                  else if (order.MaLBTT === "BNH")
                    sold[1] += Number(order.SoLuong || 0);
                  else if (order.MaLBTT === "BD")
                    sold[2] += Number(order.SoLuong || 0);
                }
              }
            });
          }
          // Ensure numeric subtraction and non-negative result
          const inventory = produced.map((qty, idx) => {
            const p = Number(qty || 0);
            const s = Number(sold[idx] || 0);
            return Math.max(0, p - s);
          });
          return {
            labels: ["Bánh Nướng", "Bánh Nhí", "Bánh Dẻo"],
            datasets: [
              {
                data: inventory,
                backgroundColor: ["#60a5fa", "#6b7280", "#ef4444"],
              },
            ],
          };
        })();
        const summedData = [0, 0, 0];
        for (let i = 0; i < 3; i++) {
          summedData[i] =
            danangInv.datasets[0].data[i] +
            hcmInv.datasets[0].data[i] +
            bonpasInv.datasets[0].data[i];
        }
        return {
          labels: ["Bánh Nướng", "Bánh Nhí", "Bánh Dẻo"],
          datasets: [
            {
              data: summedData,
              backgroundColor: ["#60a5fa", "#6b7280", "#ef4444"],
            },
          ],
        };
      }
      if (selectedLocation === "danang") {
        // Đà Nẵng: lấy từ API tồn kho như hiện tại
        return inventoryByTypeData;
      }
      if (!productionData || typeof productionData !== "object")
        return inventoryByTypeData;
      // Tính cho HCM hoặc Bonpas
      const cakeTypes = ["BNG", "BNH", "BD"];
      const locationKey = selectedLocation === "hcm" ? "HoChiMinh" : "Bonpas";
      // Sản xuất từng loại bánh
      const produced = cakeTypes.map(
        (type) => productionData[type]?.[locationKey] || 0
      );
      // Số lượng bán ra từng loại bánh (bán lẻ + bán đặt)
      let sold = [0, 0, 0];
      // Bán lẻ: từ hcmBonpasSalesData
      if (Array.isArray(hcmBonpasSalesData)) {
        hcmBonpasSalesData.forEach((item) => {
          if (item.Status === 1 && item.LoaiPhieu === 2 && item.NguoiNhan) {
            const isHcm =
              (item.NguoiNhan === 163 || item.NguoiNhan === "163") &&
              selectedLocation === "hcm";
            const isBonpas =
              (item.NguoiNhan === 1663 || item.NguoiNhan === "1663") &&
              selectedLocation === "bonpas";
            if (isHcm || isBonpas) {
              if (item.MaLBTT === "BNG") sold[0] += parseNumber(item.SoLuong);
              else if (item.MaLBTT === "BNH")
                sold[1] += parseNumber(item.SoLuong);
              else if (item.MaLBTT === "BD")
                sold[2] += parseNumber(item.SoLuong);
            }
          }
        });
      }
      // Bán đặt: từ customOrderData
      if (Array.isArray(customOrderData)) {
        customOrderData.forEach((order) => {
          if (order.Status === 1 && order.LoaiPhieu === 1 && order.NguoiNhan) {
            const isHcm =
              [
                163,
                74,
                128,
                116,
                111,
                90,
                78,
                "163",
                "74",
                "128",
                "116",
                "111",
                "90",
                "78",
              ].includes(order.NguoiNhan) && selectedLocation === "hcm";
            const isBonpas =
              [1663, 1, 3, 4, 5, 7, "1663", "1", "3", "4", "5", "7"].includes(
                order.NguoiNhan
              ) && selectedLocation === "bonpas";
            if (isHcm || isBonpas) {
              if (order.MaLBTT === "BNG") sold[0] += parseNumber(order.SoLuong);
              else if (order.MaLBTT === "BNH")
                sold[1] += parseNumber(order.SoLuong);
              else if (order.MaLBTT === "BD")
                sold[2] += parseNumber(order.SoLuong);
            }
          }
        });
      }
      // Tồn kho = sản xuất - bán ra
      const inventory = produced.map((qty, idx) => {
        const p = Number(qty || 0);
        const s = Number(sold[idx] || 0);
        return Math.max(0, p - s);
      });
      console.log("🔍 INVENTORY FOR", selectedLocation, "- labels:", [
        "Bánh Nướng",
        "Bánh Nhí",
        "Bánh Dẻo",
      ]);
      console.log("🔍 INVENTORY FOR", selectedLocation, "- data:", inventory);
      return {
        labels: ["Bánh Nướng", "Bánh Nhí", "Bánh Dẻo"],
        datasets: [
          {
            data: inventory,
            backgroundColor: ["#60a5fa", "#6b7280", "#ef4444"],
          },
        ],
      };
    };
    let banhNuongTarget = 0;
    let khacTarget = 0; // Assuming BNH corresponds to "Khác"
    let banhDeoTarget = 0;

    if (selectedLocation === "all") {
      const allKpiData = kpiData.filter((kpi) =>
        [1, 2, 3].includes(kpi.ChiNhanh)
      );
      banhNuongTarget = allKpiData.reduce(
        (sum, kpi) => sum + (kpi.BNG || 0),
        0
      );
      khacTarget = allKpiData.reduce((sum, kpi) => sum + (kpi.BNH || 0), 0);
      banhDeoTarget = allKpiData.reduce((sum, kpi) => sum + (kpi.BD || 0), 0);
    } else {
      const storeId = locationStoreMapping[selectedLocation][0];
      const kpi = kpiData.find((k) => k.ChiNhanh === storeId);
      banhNuongTarget = kpi ? kpi.BNG || 0 : 0;
      khacTarget = kpi ? kpi.BNH || 0 : 0;
      banhDeoTarget = kpi ? kpi.BD || 0 : 0;
    }

    // Calculate KPI progress
    const relevantKpiData = kpiData.filter((kpi) => {
      const storeIds = locationStoreMapping[selectedLocation];
      return storeIds.includes(kpi.ChiNhanh);
    });

    let kpiProgress = 0;
    let kpiTarget = 0;

    if (relevantKpiData.length > 0) {
      kpiTarget = relevantKpiData.reduce(
        (sum, kpi) => sum + (kpi.Target || 0),
        0
      );
    }

    // Get all KPI data for detailed logging
    const allKpiData = kpiData.filter((kpi) =>
      [1, 2, 3].includes(kpi.ChiNhanh)
    );
    const totalKpiTarget = allKpiData.reduce(
      (sum, kpi) => sum + (kpi.Target || 0),
      0
    );

    console.log("📊 TỔNG KPI:");
    console.log("- Tổng KPI target tất cả cơ sở:", totalKpiTarget);
    console.log("📋 CHI TIẾT KPI THEO CƠ SỞ:");
    allKpiData.forEach((kpi) => {
      const locationName =
        kpi.ChiNhanh === 1 ? "Đà Nẵng" : kpi.ChiNhanh === 2 ? "HCM" : "Bonpas";
      console.log(
        `- ${locationName} (ChiNhanh ${kpi.ChiNhanh}): ${kpi.Target}`
      );
    });

    // Calculate custom order sales (bánh đặt) for cumulative period from start date to selected date
    let danangCustomOrderSales = 0;
    let hcmCustomOrderSales = 0;
    let bonpasCustomOrderSales = 0;

    // Get custom orders in the selected range with status = 1 and loaiphieu = 1
    const startDate = new Date(selectedStartDate);
    const selectedDateObj = new Date(selectedEndDate);

    const cumulativeCustomOrders = customOrderData.filter(
      (order) =>
        order.Status === 1 &&
        order.LoaiPhieu === 1 &&
        !isBoxCakeType(order.MaLBTT) &&
        order.NgayGiao &&
        new Date(order.NgayGiao) >= startDate &&
        new Date(order.NgayGiao) <= selectedDateObj
    );

    // Separate by location based on NguoiNhan
    // Hàm chuẩn hóa giá trị NguoiNhan về cả số và chuỗi
    const normalize = (value) => {
      const strVal = value?.toString().trim();
      const numVal = parseInt(strVal);
      return { strVal, numVal };
    };

    // HCM group
    const isHcm = (nguoiNhan) => {
      const { strVal, numVal } = normalize(nguoiNhan);
      return (
        ["163", "74", "128", "116", "111", "90", "78"].includes(strVal) ||
        [163, 74, 128, 116, 111, 90, 78].includes(numVal)
      );
    };

    // Bonpas group
    const isBonpas = (nguoiNhan) => {
      const { strVal, numVal } = normalize(nguoiNhan);
      return (
        ["1663", "1", "3", "4", "5", "7"].includes(strVal) ||
        [1663, 1, 3, 4, 5, 7].includes(numVal)
      );
    };

    // Đà Nẵng = tất cả những gì không thuộc 2 nhóm kia
    const isDanang = (nguoiNhan) => {
      return !isHcm(nguoiNhan) && !isBonpas(nguoiNhan);
    };

    // ===================
    // Trong calculateDashboardMetrics
    cumulativeCustomOrders.forEach((order) => {
      const quantity = parseInt(order.SoLuong?.toString().trim()) || 0;
      const nguoiNhan = order.NguoiNhan;

      if (isHcm(nguoiNhan)) {
        hcmCustomOrderSales += quantity;
      } else if (isBonpas(nguoiNhan)) {
        bonpasCustomOrderSales += quantity;
      } else if (isDanang(nguoiNhan)) {
        danangCustomOrderSales += quantity;
      } else {
        console.warn(
          `Unmatched NguoiNhan: ${nguoiNhan} for order ID: ${
            order.ID || "unknown"
          }`
        );
      }
    });

    // Calculate pending custom orders (status = 0)
    let pendingDanangCustomOrderSales = 0;
    let pendingHcmCustomOrderSales = 0;
    let pendingBonpasCustomOrderSales = 0;

    const pendingCustomOrders = customOrderData.filter(
      (order) =>
        order.Status === 0 &&
        order.LoaiPhieu === 1 &&
        order.NgayDat &&
        new Date(order.NgayDat) >= startDate &&
        new Date(order.NgayDat) <= selectedDateObj
    );

    pendingCustomOrders.forEach((order) => {
      const quantity = parseInt(order.SoLuong?.toString().trim()) || 0;
      const nguoiNhan = order.NguoiNhan;

      if (isHcm(nguoiNhan)) {
        pendingHcmCustomOrderSales += quantity;
      } else if (isBonpas(nguoiNhan)) {
        pendingBonpasCustomOrderSales += quantity;
      } else if (isDanang(nguoiNhan)) {
        pendingDanangCustomOrderSales += quantity;
      }
    });

    // Calculate total sales from hoadonbantheongay (only applies to Đà Nẵng branch)
    // This should be cumulative from start date to selected date
    let totalHoadonSales = 0;
    let danangHoadonSales = 0;
    let hcmHoadonSales = 0;
    let bonpasHoadonSales = 0;

    if (hoadonbantheongayData.success && hoadonbantheongayData.grandSummary) {
      const cumulativeInvoices = getLedgerChartSalesBreakdown({
        ...hoadonbantheongayData.grandSummary.grandTotal,
        byCakeType: hoadonbantheongayData.summaryByMaLBTT,
      }).total;
      // ThongKeTongHopBanhTrungThu API only applies to Đà Nẵng branch
      danangHoadonSales += cumulativeInvoices;
    }

    // Calculate HCM and Bonpas retail sales from hcmBonpasSalesData (LoaiPhieu = 2)
    // This API returns retail sales data directly, no need to subtract custom orders
    if (hcmBonpasSalesData && Array.isArray(hcmBonpasSalesData)) {
      const cumulativeHcmBonpasSales = hcmBonpasSalesData.filter(
        (item) =>
          item.Status === 1 &&
          item.LoaiPhieu === 2 &&
          !isBoxCakeType(item.MaLBTT) &&
          item.NgayGiao &&
          new Date(item.NgayGiao) >= startDate &&
          new Date(item.NgayGiao) <= selectedDateObj
      );

      cumulativeHcmBonpasSales.forEach((item) => {
        const quantity = parseInt(item.SoLuong?.toString().trim()) || 0;
        if (isHcm(item.NguoiNhan)) {
          hcmHoadonSales += quantity;
        } else if (isBonpas(item.NguoiNhan)) {
          bonpasHoadonSales += quantity;
        }
      });
    }

    // Calculate retail sales (bánh bán lẻ):
    // - Đà Nẵng: ThongKeTongHopBanhTrungThu - custom orders (excluding HCM & Bonpas)
    // - HCM & Bonpas: Direct from soLuongBanBanhBanHCM (already retail sales)
    let danangRetailSales = Math.max(
      0,
      danangHoadonSales - danangCustomOrderSales
    );
    let hcmRetailSales = Math.max(0, hcmHoadonSales - hcmCustomOrderSales); // Previously: hcmRetailSales = hcmHoadonSales;
    let bonpasRetailSales = Math.max(
      0,
      bonpasHoadonSales - bonpasCustomOrderSales
    ); // Previously: bonpasRetailSales = bonpasHoadonSales;

    let hcmTotalSales = hcmRetailSales + hcmCustomOrderSales;
    let bonpasTotalSales = bonpasRetailSales + bonpasCustomOrderSales;

    // Calculate total sales for Đà Nẵng (retail + custom orders)
    const danangTotal = danangHoadonSales;

    console.log("🎂 TỔNG SỐ BÁNH BÁN RA (BAO GỒM BÁNH BÁN LẺ VÀ BÁNH ĐẶT):");
    const totalAllSales = danangTotal + hcmTotalSales + bonpasTotalSales;
    console.log("- Tổng tất cả cơ sở:", totalAllSales);

    console.log("🏢 TỔNG SỐ BÁNH BÁN THEO CƠ SỞ:");
    console.log(
      `- Đà Nẵng: ${danangTotal} (Bánh bán lẻ: ${danangRetailSales}, Bánh đặt: ${danangCustomOrderSales})`
    );
    console.log(
      `- HCM: ${hcmTotalSales} (Bánh bán lẻ: ${hcmRetailSales}, Bánh đặt: ${hcmCustomOrderSales})`
    );
    console.log(
      `- Bonpas: ${bonpasTotalSales} (Bánh bán lẻ: ${bonpasRetailSales}, Bánh đặt: ${bonpasCustomOrderSales})`
    );

    console.log("🛒 TỔNG SỐ BÁNH ĐẶT (BÁN SỈ) BÁN RA:");
    const totalCustomOrders =
      danangCustomOrderSales + hcmCustomOrderSales + bonpasCustomOrderSales;
    console.log("- Tổng bánh đặt:", totalCustomOrders);
    console.log("- Chi tiết theo cơ sở:");
    console.log(`  + Đà Nẵng: ${danangCustomOrderSales}`);
    console.log(`  + HCM: ${hcmCustomOrderSales}`);
    console.log(`  + Bonpas: ${bonpasCustomOrderSales}`);

    console.log("🏪 TỔNG SỐ BÁNH BÁN LẺ BÁN RA:");
    const totalRegularSales =
      danangRetailSales + hcmRetailSales + bonpasRetailSales;
    console.log("- Tổng bánh bán lẻ:", totalRegularSales);
    console.log("- Chi tiết theo cơ sở:");
    console.log(
      `  + Đà Nẵng: ${danangRetailSales} (ThongKeTongHop - BanhDat_DaNang)`
    );
    console.log(
      `  + HCM: ${hcmRetailSales} (Trực tiếp từ soLuongBanBanhBanHCM)`
    );
    console.log(
      `  + Bonpas: ${bonpasRetailSales} (Trực tiếp từ soLuongBanBanhBanHCM)`
    );

    const corporateCustomerQuantity = getCorporateCustomerQuantity(
      otherQuantities,
      selectedLocation
    );

    // Set KPI progress based on selected location
    if (selectedLocation === "all") {
      kpiProgress =
        danangTotal +
        hcmTotalSales +
        bonpasTotalSales +
        corporateCustomerQuantity;
      // KPI target is sum of all locations
      const allKpiData = kpiData.filter((kpi) =>
        [1, 2, 3].includes(kpi.ChiNhanh)
      );
      kpiTarget = allKpiData.reduce((sum, kpi) => sum + (kpi.Target || 0), 0);
    } else if (selectedLocation === "danang") {
      kpiProgress = danangTotal + corporateCustomerQuantity;
      const danangKpi = kpiData.find((kpi) => kpi.ChiNhanh === 1);
      kpiTarget = danangKpi ? danangKpi.Target : 0;
    } else if (selectedLocation === "hcm") {
      kpiProgress = hcmTotalSales;
      const hcmKpi = kpiData.find((kpi) => kpi.ChiNhanh === 2);
      kpiTarget = hcmKpi ? hcmKpi.Target : 0;
    } else if (selectedLocation === "bonpas") {
      kpiProgress = bonpasTotalSales;
      const bonpasKpi = kpiData.find((kpi) => kpi.ChiNhanh === 3);
      kpiTarget = bonpasKpi ? bonpasKpi.Target : 0;
    }

    // Build location KPI data
    const locationKPI = [];

    if (selectedLocation === "all") {
      // Get individual KPI targets for each location
      const danangKpi = kpiData.find((kpi) => kpi.ChiNhanh === 1);
      const hcmKpi = kpiData.find((kpi) => kpi.ChiNhanh === 2);
      const bonpasKpi = kpiData.find((kpi) => kpi.ChiNhanh === 3);

      locationKPI.push({
        name: "ĐN",
        value: danangTotal + corporateCustomerQuantity,
        percentage:
          danangKpi && danangKpi.Target > 0
            ? Math.round(
                ((danangTotal + corporateCustomerQuantity) / danangKpi.Target) *
                  100
              )
            : 0,
      });

      if (hcmTotalSales > 0 || (hcmKpi && hcmKpi.Target > 0)) {
        locationKPI.push({
          name: "HCM",
          value: hcmTotalSales,
          percentage:
            hcmKpi && hcmKpi.Target > 0
              ? Math.round((hcmTotalSales / hcmKpi.Target) * 100)
              : 0,
        });
      }

      if (bonpasTotalSales > 0 || (bonpasKpi && bonpasKpi.Target > 0)) {
        locationKPI.push({
          name: "Bonpas",
          value: bonpasTotalSales,
          percentage:
            bonpasKpi && bonpasKpi.Target > 0
              ? Math.round((bonpasTotalSales / bonpasKpi.Target) * 100)
              : 0,
        });
      }
    } else {
      const storeNames = { danang: "ĐN", hcm: "HCM", bonpas: "Bonpas" };
      locationKPI.push({
        name: storeNames[selectedLocation],
        value: kpiProgress,
        percentage:
          kpiTarget > 0 ? Math.round((kpiProgress / kpiTarget) * 100) : 0,
      });
    }

    // Calculate cake type breakdown from actual data
    let banhNuongTotal = 0;
    let banhDeoTotal = 0;
    let khacTotal = 0;

    // Calculate cake types for Đà Nẵng from totalSalesData
    let danangCakeTypes = { BNG: 0, BD: 0, BNH: 0 };
    if (totalSalesData.success && totalSalesData.dataByStore) {
      Object.values(totalSalesData.dataByStore).forEach((storeData) => {
        storeData.forEach((item) => {
          const quantity = item.SoLuongBan || 0;
          if (item.MaLBTT === "BNG") {
            danangCakeTypes.BNG += quantity;
          } else if (item.MaLBTT === "BD") {
            danangCakeTypes.BD += quantity;
          } else if (!isBoxCakeType(item.MaLBTT)) {
            danangCakeTypes.BNH += quantity;
          }
        });
      });
    }

    // Calculate cake types for HCM and Bonpas from hcmBonpasSalesData and customOrderData
    let hcmCakeTypes = { BNG: 0, BD: 0, BNH: 0 };
    let bonpasCakeTypes = { BNG: 0, BD: 0, BNH: 0 };
    let loyalCustomerCakeTypes = {
      danang: { BNG: 0, BD: 0, BNH: 0 },
      bonpas: { BNG: 0, BD: 0, BNH: 0 },
    };

    // From loyalCustomerData (Đà Nẵng and Bonpas loyal customers)
    loyalCustomerCakeTypes.danang = { ...loyalCustomerData.cakeType.danang };
    loyalCustomerCakeTypes.bonpas = { ...loyalCustomerData.cakeType.bonpas };

    // From hcmBonpasSalesData (retail sales)
    if (Array.isArray(hcmBonpasSalesData)) {
      hcmBonpasSalesData.forEach((item) => {
        if (
          item.Status === 1 &&
          item.LoaiPhieu === 2 &&
          item.NgayGiao &&
          new Date(item.NgayGiao) >= startDate &&
          new Date(item.NgayGiao) <= selectedDateObj
        ) {
          const quantity = parseInt(item.SoLuong?.toString().trim()) || 0;
          if (isHcm(item.NguoiNhan)) {
            if (item.MaLBTT === "BNG") hcmCakeTypes.BNG += quantity;
            else if (item.MaLBTT === "BD") hcmCakeTypes.BD += quantity;
            else if (!isBoxCakeType(item.MaLBTT)) hcmCakeTypes.BNH += quantity;
          } else if (isBonpas(item.NguoiNhan)) {
            if (item.MaLBTT === "BNG") bonpasCakeTypes.BNG += quantity;
            else if (item.MaLBTT === "BD") bonpasCakeTypes.BD += quantity;
            else if (!isBoxCakeType(item.MaLBTT))
              bonpasCakeTypes.BNH += quantity;
          }
        }
      });
    }

    // Now set the totals based on selectedLocation
    if (selectedLocation === "all") {
      banhNuongTotal =
        danangCakeTypes.BNG +
        hcmCakeTypes.BNG +
        bonpasCakeTypes.BNG +
        (loyalCustomerCakeTypes.danang.BNG || 0) +
        (loyalCustomerCakeTypes.bonpas.BNG || 0);
      banhDeoTotal =
        danangCakeTypes.BD +
        hcmCakeTypes.BD +
        bonpasCakeTypes.BD +
        (loyalCustomerCakeTypes.danang.BD || 0) +
        (loyalCustomerCakeTypes.bonpas.BD || 0);
      khacTotal =
        danangCakeTypes.BNH +
        hcmCakeTypes.BNH +
        bonpasCakeTypes.BNH +
        (loyalCustomerCakeTypes.danang.BNH || 0) +
        (loyalCustomerCakeTypes.bonpas.BNH || 0);
    } else if (selectedLocation === "danang") {
      banhNuongTotal =
        danangCakeTypes.BNG + (loyalCustomerCakeTypes.danang.BNG || 0);
      banhDeoTotal =
        danangCakeTypes.BD + (loyalCustomerCakeTypes.danang.BD || 0);
      khacTotal =
        danangCakeTypes.BNH + (loyalCustomerCakeTypes.danang.BNH || 0);
    } else if (selectedLocation === "hcm") {
      banhNuongTotal =
        hcmCakeTypes.BNG +
        (loyalCustomerCakeTypes.hcm ? loyalCustomerCakeTypes.hcm.BNG : 0);
      banhDeoTotal =
        hcmCakeTypes.BD +
        (loyalCustomerCakeTypes.hcm ? loyalCustomerCakeTypes.hcm.BD : 0);
      khacTotal =
        hcmCakeTypes.BNH +
        (loyalCustomerCakeTypes.hcm ? loyalCustomerCakeTypes.hcm.BNH : 0);
    } else if (selectedLocation === "bonpas") {
      banhNuongTotal = bonpasCakeTypes.BNG + (loyalCustomerCakeTypes.bonpas.BNG|| 0);
      banhDeoTotal = bonpasCakeTypes.BD + (loyalCustomerCakeTypes.bonpas.BD|| 0);
      khacTotal = bonpasCakeTypes.BNH + (loyalCustomerCakeTypes.bonpas.BNH || 0);
    }

    console.log("🍰 TỔNG SỐ BÁNH BÁN RA THEO LOẠI:");
    const totalCakesByType = banhNuongTotal + banhDeoTotal + khacTotal;
    console.log("- Tổng tất cả loại bánh:", totalCakesByType);
    console.log("- Chi tiết theo loại bánh:");
    console.log(
      `  + Bánh nướng (BNG): ${banhNuongTotal} (${
        totalCakesByType > 0
          ? Math.round((banhNuongTotal / totalCakesByType) * 100)
          : 0
      }%)`
    );
    console.log(
      `  + Bánh Dẻo (BD): ${banhDeoTotal} (${
        totalCakesByType > 0
          ? Math.round((banhDeoTotal / totalCakesByType) * 100)
          : 0
      }%)`
    );
    console.log(
      `  + Khác: ${khacTotal} (${
        totalCakesByType > 0
          ? Math.round((khacTotal / totalCakesByType) * 100)
          : 0
      }%)`
    );

    console.log("📈 TÍNH TOÁN KPI TIẾN ĐỘ:");
    console.log("- Location được chọn:", selectedLocation);
    console.log("- KPI Progress:", kpiProgress);
    console.log("- KPI Target:", kpiTarget);
    console.log(
      "- Phần trăm hoàn thành:",
      kpiTarget > 0 ? `${Math.round((kpiProgress / kpiTarget) * 100)}%` : "0%"
    );

    console.log("=== KẾT THÚC DASHBOARD METRICS CALCULATION ===\n");

    // Calculate production progress
    const productionTarget = productionTargets[selectedLocation];
    let totalProduction = 0;

    if (productionProgressData && typeof productionProgressData === "object") {
      // Sum Danang and Bonpas for total
      totalProduction =
        (productionProgressData.Danang || 0) +
        (productionProgressData.Bonpas || 0);
    }

    console.log("🏭 TỔNG SỐ BÁNH SẢN XUẤT (NEW API):");
    console.log("- Target sản xuất:", productionTarget);
    console.log("- Tổng đã sản xuất:", totalProduction);
    console.log(
      "- Tiến độ sản xuất:",
      totalProduction > 0
        ? `${Math.round((totalProduction / productionTarget) * 100)}%`
        : "0%"
    );

    // Calculate production by location using new API
    console.log("- Chi tiết sản xuất theo cơ sở (NEW API):");
    if (productionProgressData && typeof productionProgressData === "object") {
      console.log(`  + Đà Nẵng & HCM: ${productionProgressData.Danang || 0}`);
      console.log(`  + Bonpas: ${productionProgressData.Bonpas || 0}`);
    }

    // Build production progress data by location
    const productionProgress = [];
    if (selectedLocation === "all") {
      // For all locations, use total production
      productionProgress.push({
        name: "Tổng SX",
        value: totalProduction,
        percentage:
          productionTarget > 0
            ? Math.round((totalProduction / productionTarget) * 100)
            : 0,
      });
    } else {
      // For specific location, use the corresponding value
      const locationKeyMap = {
        danang: "Danang",
        hcm: "Danang", // Shared with Danang
        bonpas: "Bonpas",
      };
      const key = locationKeyMap[selectedLocation];
      const value = productionProgressData
        ? productionProgressData[key] || 0
        : 0;
      const storeNames = {
        danang: "ĐN & HCM",
        hcm: "ĐN & HCM",
        bonpas: "Bonpas",
      };
      productionProgress.push({
        name: storeNames[selectedLocation] || "SX",
        value: value,
        percentage:
          productionTarget > 0
            ? Math.round((value / productionTarget) * 100)
            : 0,
      });
    }

    // Calculate chart data from ThongKeTongHopBanhTrungThu (only Đà Nẵng data)
    // Calculate chart data from ThongKeTongHopBanhTrungThu (only Đà Nẵng data)
    const calculateChartDataFromInvoices = () => {
      if (
        !hoadonbantheongayData.success ||
        !hoadonbantheongayData.summaryByDate ||
        !dailyData ||
        dailyData.length === 0
      ) {
        return {
          salesTrend: [29, 35, 36, 25, 22, 17, kpiProgress || 6],
          orderTrend: [
            27,
            1,
            1,
            0,
            0,
            1,
            (selectedLocation === "danang"
              ? danangCustomOrderSales
              : selectedLocation === "hcm"
              ? hcmCustomOrderSales
              : selectedLocation === "bonpas"
              ? bonpasCustomOrderSales
              : danangCustomOrderSales +
                hcmCustomOrderSales +
                bonpasCustomOrderSales) || 2,
          ],
          cakeTrends: {
            deo: [5, 12, 35, 7, 24, 22, Math.round(kpiProgress * 0.05) || 18],
            nuong: [39, 0, 1, 1, 1, 0, Math.round(kpiProgress * 0.8) || 8],
            khac: [0, 0, 0, 28, 0, 0, Math.round(kpiProgress * 0.15) || 0],
          },
          revenue: [
            2.0,
            3.0,
            1.4,
            2.7,
            2.5,
            1.9,
            (hoadonbantheongayData.grandSummary?.grandTotal?.TongThanhTien ||
              800000) / 1000000,
          ],
          bakeryLocationData: {
            labels: [
              "75 Phan Đăng Lưu",
              "206 & 208 Quan...",
              "116 Nguyễn Phú...",
              "387 Kinh Dương...",
              "90 Văn Đồn",
              "120 Cây Keo",
              "54C Tân Quý",
            ],
            datasets: [
              {
                label: "Bánh Dẻo",
                data: [45, 46, 28, 27, 21, 3, 3],
                backgroundColor: "#ef4444",
              },
              {
                label: "Bánh nướng",
                data: [13, 8, 29, 29, 0, 0, 0],
                backgroundColor: "#60a5fa",
              },
              {
                label: "Khác",
                data: [59, 56, 0, 0, 21, 3, 3],
                backgroundColor: "#6b7280",
              },
            ],
          },
        };
      }

      const salesTrend = [];
      const orderTrend = [];
      const cakeTrends = {
        deo: [],
        nuong: [],
        khac: [],
      };
      const revenue = [];

      dailyData.forEach((dayData) => {
        const ngayGiao = dayData.date;

        // Tính bánh bán lẻ cho từng địa điểm
        let danangRetailDaily = 0;
        let hcmRetailDaily = 0;
        let bonpasRetailDaily = 0;

        // Đà Nẵng: từ ThongKeTongHopBanhTrungThu
        const dateSummary = dayData.data?.summaryByDate?.[0];
        if (dateSummary) {
          danangRetailDaily = Object.entries(dateSummary.MaLBTT || {}).reduce(
            (sum, [cakeType, quantity]) =>
              isBoxCakeType(cakeType) ? sum : sum + parseNumber(quantity),
            0
          );
        }

        // HCM & Bonpas: từ soLuongBanBanhBanHCM
        const hcmBonpasRetailDaily = hcmBonpasSalesData.filter(
          (item) =>
            item.Status === 1 &&
            !isBoxCakeType(item.MaLBTT) &&
            new Date(item.NgayGiao).toISOString().split("T")[0] === ngayGiao
        );

        hcmBonpasRetailDaily.forEach((item) => {
          const quantity = parseInt(item.SoLuong?.toString().trim()) || 0;
          if (isHcm(item.NguoiNhan)) {
            hcmRetailDaily += quantity;
          } else if (isBonpas(item.NguoiNhan)) {
            bonpasRetailDaily += quantity;
          }
        });

        // Tính bánh đặt theo ngày
        const customOrdersDaily = customOrderData.filter(
          (order) =>
            order.Status === 1 &&
            !isBoxCakeType(order.MaLBTT) &&
            new Date(order.NgayGiao).toISOString().split("T")[0] === ngayGiao
        );

        let danangCustomDaily = 0;
        let hcmCustomDaily = 0;
        let bonpasCustomDaily = 0;

        customOrdersDaily.forEach((order) => {
          const quantity = parseInt(order.SoLuong?.toString().trim()) || 0;
          const nguoiNhan = order.NguoiNhan;

          if (isHcm(nguoiNhan)) {
            hcmCustomDaily += quantity;
          } else if (isBonpas(nguoiNhan)) {
            bonpasCustomDaily += quantity;
          } else if (isDanang(nguoiNhan)) {
            danangCustomDaily += quantity;
          } else {
            console.warn(
              `Unmatched NguoiNhan: ${nguoiNhan} for order ID: ${
                order.ID || "unknown"
              }`
            );
          }
        });

        // Cập nhật dữ liệu theo location được chọn
        let dailySales = 0;
        let dailyOrders = 0;

        if (selectedLocation === "all") {
          dailySales =
            danangRetailDaily -
            danangCustomDaily +
            hcmRetailDaily +
            bonpasRetailDaily;
          dailyOrders = danangCustomDaily + hcmCustomDaily + bonpasCustomDaily;
        } else if (selectedLocation === "danang") {
          dailySales = danangRetailDaily - danangCustomDaily; // Bánh bán lẻ thực tế
          dailyOrders = danangCustomDaily;
        } else if (selectedLocation === "hcm") {
          dailySales = hcmRetailDaily;
          dailyOrders = hcmCustomDaily;
        } else if (selectedLocation === "bonpas") {
          dailySales = bonpasRetailDaily;
          dailyOrders = bonpasCustomDaily;
        }

        salesTrend.push(dailySales);
        orderTrend.push(dailyOrders);

        // Tính cakeTrends theo loại bánh
        let dailyDeo = 0,
          dailyNuong = 0,
          dailyKhac = 0;

        // Từ Đà Nẵng data (ThongKeTongHopBanhTrungThu)
        if (
          dateSummary &&
          (selectedLocation === "all" || selectedLocation === "danang")
        ) {
          dailyDeo += dateSummary.MaLBTT?.BD || 0;
          dailyNuong += dateSummary.MaLBTT?.BNG || 0;
          dailyKhac += dateSummary.MaLBTT?.BNH || 0;
        }

        // Từ HCM/Bonpas retail sales
        if (
          selectedLocation === "all" ||
          selectedLocation === "hcm" ||
          selectedLocation === "bonpas"
        ) {
          hcmBonpasRetailDaily.forEach((item) => {
            const quantity = parseInt(item.SoLuong?.toString().trim()) || 0;
            // Chỉ tính nếu location match
            if (
              selectedLocation === "all" ||
              (selectedLocation === "hcm" && isHcm(item.NguoiNhan)) ||
              (selectedLocation === "bonpas" && isBonpas(item.NguoiNhan))
            ) {
              if (item.MaLBTT === "BD") dailyDeo += quantity;
              else if (item.MaLBTT === "BNG") dailyNuong += quantity;
              else if (!isBoxCakeType(item.MaLBTT)) dailyKhac += quantity;
            }
          });
        }

        // Từ custom orders
        if (
          selectedLocation === "all" ||
          selectedLocation === "danang" ||
          selectedLocation === "hcm" ||
          selectedLocation === "bonpas"
        ) {
          customOrdersDaily.forEach((order) => {
            const quantity = parseInt(order.SoLuong?.toString().trim()) || 0;
            // Chỉ tính nếu location match
            if (
              selectedLocation === "all" ||
              (selectedLocation === "hcm" && isHcm(order.NguoiNhan)) ||
              (selectedLocation === "bonpas" && isBonpas(order.NguoiNhan))
            ) {
              if (order.MaLBTT === "BD") dailyDeo += quantity;
              else if (order.MaLBTT === "BNG") dailyNuong += quantity;
              else if (!isBoxCakeType(order.MaLBTT)) dailyKhac += quantity;
            }
          });
        }

        cakeTrends.deo.push(dailyDeo);
        cakeTrends.nuong.push(dailyNuong);
        cakeTrends.khac.push(dailyKhac);

        // Tính doanh số theo ngày
        let dailyRevenue = 0;

        // Doanh số từ Đà Nẵng
        const danangRevenueForDay = danangRevenueData.find(
          (item) => new Date(item.Ngay).toISOString().split("T")[0] === ngayGiao // Normalize date to YYYY-MM-DD
        );
        console.log(
          `🔍 DEBUG - For date ${ngayGiao}, danangRevenueForDay:`,
          danangRevenueForDay
        );
        if (
          danangRevenueForDay &&
          (selectedLocation === "all" || selectedLocation === "danang")
        ) {
          // Use the correct field from DoanhThu7NgayGanNhat API
          const doanhThu =
            danangRevenueForDay.TongDoanhThu ||
            danangRevenueForDay.DoanhThu ||
            0;
          console.log(
            `🔍 DEBUG - Using TongDoanhThu/DoanhThu for Đà Nẵng on ${ngayGiao}: ${doanhThu}`
          );
          dailyRevenue += doanhThu;
        } else if (selectedLocation === "danang") {
          // If no data from DoanhThu7NgayGanNhat for Đà Nẵng, log and skip (do not use hoadonbantheongay)
          console.warn(
            `⚠️ WARNING - No Đà Nẵng revenue data found for ${ngayGiao} in DoanhThu7NgayGanNhat API. Skipping revenue addition.`
          );
        }

        // Doanh số từ HCM/Bonpas retail
        if (
          selectedLocation === "all" ||
          selectedLocation === "hcm" ||
          selectedLocation === "bonpas"
        ) {
          hcmBonpasRetailDaily.forEach((item) => {
            if (
              selectedLocation === "all" ||
              (selectedLocation === "hcm" && isHcm(item.NguoiNhan)) ||
              (selectedLocation === "bonpas" && isBonpas(item.NguoiNhan))
            ) {
              dailyRevenue += item.ThanhTien || 0;
            }
          });
        }

        // Doanh số từ custom orders
        if (
          selectedLocation === "all" ||
          selectedLocation === "danang" ||
          selectedLocation === "hcm" ||
          selectedLocation === "bonpas"
        ) {
          customOrdersDaily.forEach((order) => {
            if (
              selectedLocation === "all" ||
              (selectedLocation === "hcm" && isHcm(order.NguoiNhan)) ||
              (selectedLocation === "bonpas" && isBonpas(order.NguoiNhan)) ||
              (selectedLocation === "danang" && isDanang(order.NguoiNhan))
            ) {
              dailyRevenue += order.ThanhTien || 0;
            }
          });
        }

        revenue.push(dailyRevenue / 1000000); // Convert to millions
        console.log(
          `🔍 DEBUG - Final dailyRevenue for ${ngayGiao}: ${dailyRevenue} (chart value: ${
            dailyRevenue / 1000000
          }M)`
        ); // Log final value
      });

      return {
        salesTrend,
        orderTrend,
        cakeTrends,
        revenue,
        last7DaysLabels: dailyData.map((day) => {
          const date = new Date(day.date);
          return `${date.getMonth() + 1}/${date.getDate()}`;
        }),
        bakeryLocationData: {
          labels: ["Bakery 1", "Bakery 2", "Bakery 3"],
          datasets: [
            {
              label: "Bánh Dẻo",
              data: [10, 20, 30],
              backgroundColor: "#ef4444",
            },
            {
              label: "Bánh nướng",
              data: [15, 25, 35],
              backgroundColor: "#60a5fa",
            },
            {
              label: "Khác",
              data: [5, 10, 15],
              backgroundColor: "#6b7280",
            },
          ],
        },
      };
    };

    const chartData = calculateChartDataFromInvoices();

    return {
      kpiProgress,
      kpiTarget: kpiTarget || 200,
      locationKPI,
      orderTypeKPI: [
        {
          name: "Bánh bán",
          value:
            selectedLocation === "danang"
              ? danangRetailSales
              : selectedLocation === "hcm"
              ? hcmRetailSales
              : selectedLocation === "bonpas"
              ? bonpasRetailSales
              : danangRetailSales + hcmRetailSales + bonpasRetailSales,
          percentage: (() => {
            let banhBanTarget = 0;
            const banhBanValue =
              selectedLocation === "danang"
                ? danangRetailSales
                : selectedLocation === "hcm"
                ? hcmRetailSales
                : selectedLocation === "bonpas"
                ? bonpasRetailSales
                : danangRetailSales + hcmRetailSales + bonpasRetailSales;

            if (selectedLocation === "all") {
              const allKpiData = kpiData.filter((kpi) =>
                [1, 2, 3].includes(kpi.ChiNhanh)
              );
              banhBanTarget = allKpiData.reduce(
                (sum, kpi) => sum + (kpi.BanhBan || 0),
                0
              );
            } else {
              const storeId = locationStoreMapping[selectedLocation][0];
              const kpi = kpiData.find((k) => k.ChiNhanh === storeId);
              banhBanTarget = kpi ? kpi.BanhBan || 0 : 0;
            }

            return banhBanTarget > 0
              ? Math.round((banhBanValue / banhBanTarget) * 100)
              : 0;
          })(),
        },
        {
          name: "Bánh đặt",
          value:
            selectedLocation === "danang"
              ? danangCustomOrderSales
              : selectedLocation === "hcm"
              ? hcmCustomOrderSales
              : selectedLocation === "bonpas"
              ? bonpasCustomOrderSales
              : danangCustomOrderSales +
                hcmCustomOrderSales +
                bonpasCustomOrderSales,
          percentage: (() => {
            let banhDatTarget = 0;
            const banhDatValue =
              selectedLocation === "danang"
                ? danangCustomOrderSales
                : selectedLocation === "hcm"
                ? hcmCustomOrderSales
                : selectedLocation === "bonpas"
                ? bonpasCustomOrderSales
                : danangCustomOrderSales +
                  hcmCustomOrderSales +
                  bonpasCustomOrderSales;

            if (selectedLocation === "all") {
              const allKpiData = kpiData.filter((kpi) =>
                [1, 2, 3].includes(kpi.ChiNhanh)
              );
              banhDatTarget = allKpiData.reduce(
                (sum, kpi) => sum + (kpi.BanhDat || 0),
                0
              );
            } else {
              const storeId = locationStoreMapping[selectedLocation][0];
              const kpi = kpiData.find((k) => k.ChiNhanh === storeId);
              banhDatTarget = kpi ? kpi.BanhDat || 0 : 0;
            }

            return banhDatTarget > 0
              ? Math.round((banhDatValue / banhDatTarget) * 100)
              : 0;
          })(),
        },
        {
          name: "Bánh đặt PBBP",
          value: pbbpOrderStatusSummary.total,
          unconfirmedValue: pbbpOrderStatusSummary.unconfirmed,
          depositedPendingConfirmationValue:
            pbbpOrderStatusSummary.depositedPendingConfirmation,
          confirmedCompletedValue: pbbpOrderStatusSummary.confirmedCompleted,
          percentage: 0, // No KPI target for loyal customers
        },
      ],
      cakeTypeKPI: [
        {
          name: "Bánh nướng",
          value: banhNuongTotal,
          percentage:
            banhNuongTarget > 0
              ? Math.round((banhNuongTotal / banhNuongTarget) * 100)
              : 0,
        },
        {
          name: "Khác",
          value: khacTotal,
          percentage:
            khacTarget > 0 ? Math.round((khacTotal / khacTarget) * 100) : 0,
        },
        {
          name: "Bánh Dẻo",
          value: banhDeoTotal,
          percentage:
            banhDeoTarget > 0
              ? Math.round((banhDeoTotal / banhDeoTarget) * 100)
              : 0,
        },
      ],
      productionProgress: productionProgress,
      salesTrend: chartData.salesTrend,
      orderTrend: chartData.orderTrend,
      cakeTrends: chartData.cakeTrends,
      revenue: chartData.revenue,
      last7DaysLabels: chartData.last7DaysLabels,
      bakeryLocationData: chartData.bakeryLocationData,
      inventoryByType: getInventoryByTypeForLocation(),
    };
  };

  // Function to filter and sort chart data by total quantity
  const getFilteredData = (data, topN) => {
    if (topN === "all") return data;

    const totals = data.labels.map((label, index) => {
      const total = data.datasets.reduce(
        (sum, dataset) => sum + dataset.data[index],
        0
      );
      return { label, total, index };
    });

    totals.sort((a, b) => b.total - a.total);

    const topItems = totals.slice(0, topN);

    const newLabels = topItems.map((item) => item.label);
    const newDatasets = data.datasets.map((dataset) => ({
      ...dataset,
      data: topItems.map((item) => dataset.data[item.index]),
    }));

    return { labels: newLabels, datasets: newDatasets };
  };

  // Filtered data for charts
  const filteredBakeryInventoryData = getFilteredData(
    bakeryInventoryData,
    topN
  );
  const filteredBakeryLocationData = getFilteredData(bakeryLocationData, topN);

  // Get dynamic data or fallback to mock data
  const currentData = calculateDashboardMetrics();

  // Mock data cho các biểu đồ - sử dụng dữ liệu động
  const salesTrendData = {
    labels: currentData.last7DaysLabels,
    datasets: [
      {
        label: "Bánh bán",
        data: currentData.salesTrend,
        backgroundColor: "#1e3a8a",
      },
      {
        label: "Bánh đặt",
        data: currentData.orderTrend,
        backgroundColor: "#fbbf24",
      },
    ],
  };

  const salesTrendByTypeData = {
    labels: currentData.last7DaysLabels,
    datasets: [
      {
        label: "Bánh Dẻo",
        data: currentData.cakeTrends.deo,
        backgroundColor: "#ef4444",
      },
      {
        label: "Bánh nướng",
        data: currentData.cakeTrends.nuong,
        backgroundColor: "#60a5fa",
      },
      {
        label: "Khác",
        data: currentData.cakeTrends.khac,
        backgroundColor: "#6b7280",
      },
    ],
  };

  const pbbpStatusItem =
    currentData.orderTypeKPI.find((item) => item.name === "Bánh đặt PBBP") ||
    {};
  const pbbpStatusValues = [
    pbbpStatusItem.unconfirmedValue || 0,
    pbbpStatusItem.depositedPendingConfirmationValue || 0,
    pbbpStatusItem.confirmedCompletedValue || 0,
  ];
  const pbbpStatusTotal = pbbpStatusValues.reduce(
    (total, value) => total + value,
    0
  );
  const otherQuantityValues = [
    otherQuantities.BieuTang,
    otherQuantities.ThanhLyBan,
    otherQuantities.KhoQC,
  ];
  const pmAndOtherQuantityTotal = [
    ...pbbpStatusValues,
    ...otherQuantityValues,
  ].reduce((total, value) => total + parseNumber(value), 0);
  const pmKpiSegments = [
    { label: "PM Đặt Hàng", value: pbbpStatusTotal, color: "#3b82f6" },
    { label: "Thanh lý", value: otherQuantities.ThanhLyBan, color: "#ec4899" },
    { label: "Trả hủy", value: otherQuantities.KhoQC, color: "#f59e0b" },
    { label: "Biếu tặng", value: otherQuantities.BieuTang, color: "#10b981" },
  ];
  const pmKpiCircleBackground = buildProgressCircleBackground(
    pmKpiSegments,
    currentData.kpiTarget
  );
  const pbbpStatusData = {
    labels: [
      "Chưa xác nhận",
      "Chưa xác nhận nhưng đã cọc",
      "Đã xác nhận và hoàn thành",
    ],
    datasets: [
      {
        data: pbbpStatusValues,
        backgroundColor: ["#94a3b8", "#fbbf24", "#22c55e"],
        borderColor: "#ffffff",
        borderWidth: 2,
      },
    ],
  };

  const revenueData = {
    labels: currentData.last7DaysLabels,
    datasets: [
      {
        label: "Doanh số",
        data: currentData.revenue,
        borderColor: "#3b82f6",
        backgroundColor: "#3b82f6",
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        grid: {
          display: true,
        },
        beginAtZero: true,
      },
    },
  };

  const horizontalChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: {
        position: "bottom",
      },
    },
    scales: {
      x: {
        stacked: true,
        beginAtZero: true,
      },
      y: {
        stacked: true,
        ticks: {
          callback: function (value, index) {
            const label = this.getLabelForValue(value);
            return label.length > 15 ? label.substring(0, 15) + "..." : label;
          },
        },
      },
    },
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return value + "M";
          },
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
  };

  // Fetch production data
  useEffect(() => {
    // Remove this useEffect completely since production data is already fetched in main useEffect
  }, []);

  // Add authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [allowedLocations, setAllowedLocations] = useState([]);

  // Function to check if authentication is still valid (within 1 hour)
  const checkAuthentication = () => {
    const loginTime = localStorage.getItem("loginTime");
    const storedAllowedLocations = localStorage.getItem("allowedLocations");
    const storedSelectedLocation = localStorage.getItem("selectedLocation");

    if (loginTime && storedAllowedLocations && storedSelectedLocation) {
      const currentTime = new Date().getTime();
      const loginTimestamp = parseInt(loginTime, 10);
      const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds

      if (currentTime - loginTimestamp < oneHour) {
        setAllowedLocations(JSON.parse(storedAllowedLocations));
        setSelectedLocation(storedSelectedLocation);
        setIsAuthenticated(true);
      } else {
        // Expired, clear localStorage
        localStorage.removeItem("loginTime");
        localStorage.removeItem("allowedLocations");
        localStorage.removeItem("selectedLocation");
        setIsAuthenticated(false);
      }
    }
  };

  // Check authentication on component mount
  useEffect(() => {
    checkAuthentication();
  }, []);

  // Function to handle login
  const handleLogin = () => {
    if (password === "15082026" || password === "3042001") {
      setAllowedLocations(["all", "danang", "hcm", "bonpas"]);
      setSelectedLocation("all");
      setIsAuthenticated(true);
      setLoginError("");
      // Store in localStorage with current time
      localStorage.setItem("loginTime", new Date().getTime().toString());
      localStorage.setItem(
        "allowedLocations",
        JSON.stringify(["all", "danang", "hcm", "bonpas"])
      );
      localStorage.setItem("selectedLocation", "all");
    } else if (password === "DongTien@999#") {
      setAllowedLocations(["danang"]);
      setSelectedLocation("danang");
      setIsAuthenticated(true);
      setLoginError("");
      localStorage.setItem("loginTime", new Date().getTime().toString());
      localStorage.setItem("allowedLocations", JSON.stringify(["danang"]));
      localStorage.setItem("selectedLocation", "danang");
    } else if (password === "Bonpas@999#") {
      setAllowedLocations(["bonpas"]);
      setSelectedLocation("bonpas");
      setIsAuthenticated(true);
      setLoginError("");
      localStorage.setItem("loginTime", new Date().getTime().toString());
      localStorage.setItem("allowedLocations", JSON.stringify(["bonpas"]));
      localStorage.setItem("selectedLocation", "bonpas");
    } else if (password === "Hochiminh@999#") {
      setAllowedLocations(["hcm"]);
      setSelectedLocation("hcm");
      setIsAuthenticated(true);
      setLoginError("");
      localStorage.setItem("loginTime", new Date().getTime().toString());
      localStorage.setItem("allowedLocations", JSON.stringify(["hcm"]));
      localStorage.setItem("selectedLocation", "hcm");
    } else {
      setLoginError("Mật khẩu không đúng. Vui lòng thử lại.");
    }
  };

  // If not authenticated, show login form
  if (!isAuthenticated) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            padding: "40px",
            borderRadius: "10px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            textAlign: "center",
            width: "300px",
          }}
        >
          <h2>Đăng nhập</h2>
          <input
            type="password"
            placeholder="Nhập mật khẩu"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{
              width: "100%",
              padding: "10px",
              margin: "10px 0",
              border: "1px solid #ccc",
              borderRadius: "5px",
              fontSize: "16px",
            }}
          />
          {loginError && (
            <p style={{ color: "red", margin: "10px 0" }}>{loginError}</p>
          )}
          <button
            onClick={handleLogin}
            style={{
              width: "100%",
              padding: "10px",
              backgroundColor: "#3b82f6",
              color: "white",
              border: "none",
              borderRadius: "5px",
              fontSize: "16px",
              cursor: "pointer",
            }}
          >
            Đăng nhập
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <NavbarTrungThu />
      <div className="dashboard">
        <div className="dashboard-header">
          <div className="time-filter">
            <label>
              Từ ngày
              <input
                type="date"
                value={selectedStartDate}
                max={selectedEndDate}
                required
                onChange={(e) => {
                  if (e.target.value) setSelectedStartDate(e.target.value);
                }}
                style={{
                  padding: "8px 12px",
                  marginLeft: "6px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  backgroundColor: "white",
                  fontSize: "14px",
                }}
              />
            </label>
            <label style={{ marginLeft: "12px" }}>
              Đến ngày
              <input
                type="date"
                value={selectedEndDate}
                min={selectedStartDate}
                required
                onChange={(e) => {
                  if (e.target.value) setSelectedEndDate(e.target.value);
                }}
                style={{
                  padding: "8px 12px",
                  marginLeft: "6px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  backgroundColor: "white",
                  fontSize: "14px",
                }}
              />
            </label>
          </div>
          <div className="location-filter">
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              {allowedLocations.map((loc) => (
                <option key={loc} value={loc}>
                  {loc === "all"
                    ? "Tất cả cơ sở"
                    : loc === "danang"
                    ? "Đà Nẵng"
                    : loc === "hcm"
                    ? "Hồ Chí Minh"
                    : "Bonpas"}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="kpi-section">
          <div className="kpi-card kpi-scan-progress-card">
            <div
              className="kpi-circle"
              style={{
                "--progress-angle": `${Math.min(
                  (currentData.kpiProgress / currentData.kpiTarget) * 360,
                  360
                )}deg`,
              }}
            >
              <span className="kpi-number">
                {Math.round(
                  (currentData.kpiProgress / currentData.kpiTarget) * 100
                )}
                %
              </span>
            </div>
            <h3 style={{ textAlign: "center" }}>
              Tiến độ đạt KPI theo lượng quét: <br/> {currentData.kpiProgress} /{" "}
              {currentData.kpiTarget}
            </h3>
             <div className="kpi-details">
              <span>Số bánh đã quét bán</span>
              <span>/ KPI mùa vụ</span>
            </div>
          </div>

          <div className="kpi-card kpi-pm-progress-card">
            <div
              className="kpi-circle"
              style={{
                "--progress-angle": `${Math.min(
                  (pmAndOtherQuantityTotal / currentData.kpiTarget) * 360,
                  360
                )}deg`,
                background: pmKpiCircleBackground,
              }}
              title={pmKpiSegments
                .map((segment) => `${segment.label}: ${segment.value}`)
                .join("\n")}
            >
              <span className="kpi-number">
                {currentData.kpiTarget > 0
                  ? Math.round(
                      (pmAndOtherQuantityTotal / currentData.kpiTarget) * 100
                    )
                  : 0}
                %
              </span>
            </div>
            <h3 style={{ textAlign: "center" }}>
              Tiến độ KPI trên PM:<br/> {pmAndOtherQuantityTotal} /{" "}
              {currentData.kpiTarget}
            </h3>
            <div className="kpi-details">
              <span>(Đặt hàng + Số lượng khác)</span>
              <span>/ KPI mùa vụ</span>
            </div>
          </div>

          <div className="kpi-card kpi-location-card">
            <h3>Tiến độ đạt KPI theo cơ sở</h3>
            <div className="kpi-bars">
              {currentData.locationKPI.map((item, index) => {
                // Get KPI target for tooltip
                let kpiTarget = 0;
                if (selectedLocation === "all") {
                  const storeId =
                    item.name === "ĐN" ? 1 : item.name === "HCM" ? 2 : 3;
                  const kpi = dashboardData.kpiData.find(
                    (k) => k.ChiNhanh === storeId
                  );
                  kpiTarget = kpi ? kpi.Target : 0;
                } else {
                  const storeId = locationStoreMapping[selectedLocation][0];
                  const kpi = dashboardData.kpiData.find(
                    (k) => k.ChiNhanh === storeId
                  );
                  kpiTarget = kpi ? kpi.Target : 0;
                }

                return (
                  <div key={index} className="kpi-bar">
                    <span>{item.name}</span>
                    <div className="bar-container">
                      <div
                        className="bar"
                        style={{
                          width: `${Math.min(item.percentage, 100)}%`,
                          backgroundColor: "#60a5fa",
                        }}
                        title={`Đã đạt: ${item.value} / KPI: ${kpiTarget} (${item.percentage}%)`}
                      >
                        {item.value}
                      </div>
                    </div>
                    <span>{item.percentage}%</span>
                  </div>
                );
              })}
            </div>
            <div className="kpi-details">
              <span>
                Quét trên PM + Khách hàng xuất từ T7.
              </span>
            </div>
          </div>

          <div className="kpi-card kpi-order-type-card">
            <h3>Tiến độ theo loại đơn (Quét trên PM)</h3>
            <div className="kpi-bars">
              {currentData.orderTypeKPI
                .filter((item) => item.name !== "Bánh đặt PBBP")
                .map((item, index) => {
                  // Get KPI target for tooltip
                  let kpiTarget = 0;
                  const orderTypeCode =
                    item.name === "Bánh bán" ? "BanhBan" : "BanhDat";

                  if (selectedLocation === "all") {
                    const allKpiData = dashboardData.kpiData.filter((kpi) =>
                      [1, 2, 3].includes(kpi.ChiNhanh)
                    );
                    kpiTarget = allKpiData.reduce(
                      (sum, kpi) => sum + (kpi[orderTypeCode] || 0),
                      0
                    );
                  } else {
                    const storeId = locationStoreMapping[selectedLocation][0];
                    const kpi = dashboardData.kpiData.find(
                      (k) => k.ChiNhanh === storeId
                    );
                    kpiTarget = kpi ? kpi[orderTypeCode] || 0 : 0;
                  }

                  return (
                    <div key={index} className="kpi-bar">
                      <span>{item.name}</span>
                      <div className="bar-container">
                        <div
                          className="bar"
                          style={{
                            width: `${Math.min(item.percentage, 100)}%`,
                            backgroundColor:
                              item.name === "Bánh bán" ? "#1e3a8a" : "#60a5fa",
                          }}
                          title={`Đã đạt: ${item.value} ${
                            item.percentage ? `/ KPI: ${kpiTarget}` : ""
                          }`}
                        >
                          {item.value}
                        </div>
                      </div>
                      <span>{item.percentage}%</span>
                    </div>
                  );
              })}
            </div>
            <div className="kpi-details">
              <span>
                Tổng số lượng đã quét tại BKR
              </span>
            </div>
          </div>

          <div className="kpi-card kpi-cake-type-card">
            <h3>Tiến độ đạt KPI theo loại bánh</h3>
            <div className="kpi-bars">
              {currentData.cakeTypeKPI.map((item, index) => {
                // Get KPI target for tooltip
                let kpiTarget = 0;
                const cakeTypeCode =
                  item.name === "Bánh nướng"
                    ? "BNG"
                    : item.name === "Bánh Dẻo"
                    ? "BD"
                    : "BNH";

                if (selectedLocation === "all") {
                  const allKpiData = dashboardData.kpiData.filter((kpi) =>
                    [1, 2, 3].includes(kpi.ChiNhanh)
                  );
                  kpiTarget = allKpiData.reduce(
                    (sum, kpi) => sum + (kpi[cakeTypeCode] || 0),
                    0
                  );
                } else {
                  const storeId = locationStoreMapping[selectedLocation][0];
                  const kpi = dashboardData.kpiData.find(
                    (k) => k.ChiNhanh === storeId
                  );
                  kpiTarget = kpi ? kpi[cakeTypeCode] || 0 : 0;
                }

                return (
                  <div key={index} className="kpi-bar">
                    <span>{item.name}</span>
                    <div className="bar-container">
                      <div
                        className="bar"
                        style={{
                          width: `${Math.min(item.percentage, 100)}%`,
                          backgroundColor:
                            item.name === "Bánh nướng"
                              ? "#60a5fa"
                              : item.name === "Khác"
                              ? "#6b7280"
                              : "#ef4444",
                        }}
                        title={`Đã đạt: ${item.value} / KPI: ${kpiTarget} (${item.percentage}%)`}
                      >
                        {item.value}
                      </div>
                    </div>
                    <span>{item.percentage}%</span>
                  </div>
                );
              })}
            </div>
            <div className="kpi-details">
              <span>
                Quét trên PM + Khách hàng xuất từ T7.
              </span>
            </div>
          </div>

          <div className="kpi-card kpi-production-card">
            <h3>Tiến độ sản xuất</h3>
            <div className="kpi-bars">
              {(() => {
                // Use productionProgress from currentData (calculated in calculateDashboardMetrics)
                const productionProgressData =
                  currentData.productionProgress || [];
                const productionTarget = productionTargets[selectedLocation];

                // If dashboard is still loading, show loading state
                if (
                  loadingStates.dashboard ||
                  productionProgressData.length === 0
                ) {
                  return (
                    <div className="kpi-bar">
                      <span>Đang tải...</span>
                      <div className="bar-container">
                        <div
                          className="bar"
                          style={{
                            width: "0%",
                            backgroundColor: "#fbbf24",
                          }}
                        >
                          0
                        </div>
                      </div>
                      <span>0%</span>
                    </div>
                  );
                }

                return productionProgressData.map((item, index) => {
                  // Ensure numeric values
                  const value = Number(item.value) || 0;
                  const percentage = Number(item.percentage) || 0;

                  return (
                    <div key={index} className="kpi-bar">
                      <span>{item.name}</span>
                      <div className="bar-container">
                        <div
                          className="bar"
                          style={{
                            width: `${Math.min(percentage, 100)}%`,
                            backgroundColor: "#fbbf24",
                          }}
                          title={`Đã sản xuất: ${value} / Target: ${productionTarget} (${percentage}%)`}
                        >
                          {value}
                        </div>
                      </div>
                      <span>{percentage}%</span>
                    </div>
                  );
                });
              })()}
            </div>
            <div className="kpi-details">
              <span>Quét nhập vào kho tổng</span>
            </div>
          </div>
        </div>

        <div className="charts-section">
          <div
            className="chart-row dashboard-overview-chart-row"
            style={{
              "--overview-chart-columns": 5,
            }}
          >
            <div className="chart-container">
              <h3>Xu hướng lượng bán theo loại đơn</h3>
              <div className="chart-wrapper" style={{ height: "350px" }}>
                {loadingStates.dashboard ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <div className="loading-text">Đang tải dữ liệu...</div>
                  </div>
                ) : (
                  <Bar data={salesTrendData} options={chartOptions} />
                )}
              </div>
            </div>

            <div className="chart-container">
              <h3>Xu hướng lượng bán theo loại bánh</h3>
              <div className="chart-wrapper" style={{ height: "350px" }}>
                {loadingStates.dashboard ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <div className="loading-text">Đang tải dữ liệu...</div>
                  </div>
                ) : (
                  <Bar data={salesTrendByTypeData} options={chartOptions} />
                )}
              </div>
            </div>

            <div className="chart-container">
              <h3>
                Số lượng trên PM Đặt Hàng ({pbbpStatusTotal})
              </h3>
              <div className="chart-wrapper" style={{ height: "350px" }}>
                {loadingStates.dashboard ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <div className="loading-text">Đang tải dữ liệu...</div>
                  </div>
                ) : pbbpStatusTotal > 0 ? (
                  <Doughnut data={pbbpStatusData} options={doughnutOptions} />
                ) : (
                  <div className="loading-container">Không có dữ liệu</div>
                )}
              </div>
            </div>

            <div
              className="chart-container dashboard-bt-chart-container"
            >
              <BT_BDNB
                selectedLocation={selectedLocation}
                ngayBatDau={selectedStartDate}
                ngayKetThuc={selectedEndDate}
                onDataChange={setOtherQuantities}
              />
            </div>

            <div className="chart-container">
              <h3>Tồn theo loại bánh</h3>
              <div className="chart-wrapper" style={{ height: "350px" }}>
                {loadingStates.inventory ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <div className="loading-text">Đang tải dữ liệu...</div>
                  </div>
                ) : (
                  <Doughnut
                    data={currentData.inventoryByType}
                    options={doughnutOptions}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="chart-row">
            <div className="chart-container">
              <h3>Xu hướng doanh số</h3>
              <div className="chart-wrapper" style={{ height: "350px" }}>
                {loadingStates.dashboard ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <div className="loading-text">Đang tải dữ liệu...</div>
                  </div>
                ) : (
                  <Line data={revenueData} options={lineChartOptions} />
                )}
              </div>
            </div>

            <div className="chart-container">
              <h3>Lượng bán theo Bakery và loại bánh</h3>
              <div className="chart-wrapper" style={{ height: "350px" }}>
                {loadingStates.bakeryLocation ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <div className="loading-text">Đang tải dữ liệu...</div>
                  </div>
                ) : (
                  <Bar
                    data={filteredBakeryLocationData}
                    options={horizontalChartOptions}
                  />
                )}
              </div>
            </div>
            <div className="chart-container">
              <h3>Lượng tồn theo Bakery và loại bánh</h3>
              <div className="chart-wrapper" style={{ height: "350px" }}>
                {loadingStates.inventory ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <div className="loading-text">Đang tải dữ liệu...</div>
                  </div>
                ) : (
                  <Bar
                    data={filteredBakeryInventoryData}
                    options={horizontalChartOptions}
                  />
                )}
              </div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr" }}>
            <div></div>
            <div>
              <div
                className="top-buttons"
                style={{ marginBottom: "20px", textAlign: "center" }}
              >
                <button
                  onClick={() => setTopN(5)}
                  style={{
                    padding: "8px 16px",
                    margin: "0 5px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    backgroundColor: topN === 5 ? "#3b82f6" : "white",
                    color: topN === 5 ? "white" : "black",
                    cursor: "pointer",
                  }}
                >
                  Xem top 5
                </button>
                <button
                  onClick={() => setTopN(10)}
                  style={{
                    padding: "8px 16px",
                    margin: "0 5px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    backgroundColor: topN === 10 ? "#3b82f6" : "white",
                    color: topN === 10 ? "white" : "black",
                    cursor: "pointer",
                  }}
                >
                  Xem top 10
                </button>
                <button
                  onClick={() => setTopN("all")}
                  style={{
                    padding: "8px 16px",
                    margin: "0 5px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    backgroundColor: topN === "all" ? "#3b82f6" : "white",
                    color: topN === "all" ? "white" : "black",
                    cursor: "pointer",
                  }}
                >
                  Xem tất cả
                </button>
              </div>
            </div>
          </div>
          <KPIPerDayChart
            chiNhanh={KPI_LOCATION_STORE_MAPPING[selectedLocation]}
          />
          <KPIPerPharseChart
            chiNhanh={KPI_LOCATION_STORE_MAPPING[selectedLocation]}
          />
        </div>
      </div>
    </>
  );
};

export default DashboardDataNew;
